import { c as _c } from "react/compiler-runtime";
import CircularPercentage from '@/comps/CircularPercentage';
import { Chip } from '@/comps/chip';
import { BottomCtaPortal } from '@/comps/ctas';
import { DialogConfirm, DialogConfirmLoading } from '@/comps/dialog-confirm';
import { LoadingSpinner, WithLoadingContainer } from '@/comps/loading-spinner';
import { LabelPicker } from '@/comps/label-picker/LabelPicker';
import { Button } from '@/comps/primitives/button';
import { Checkbox } from '@/comps/primitives/checkbox';
import { Dialog, DialogClose, DialogContentWrapper, DialogTitle, DialogTrigger } from '@/comps/primitives/dialog';
import { Input } from '@/comps/primitives/input';
import { TooltipButton } from '@/comps/tooltip-button';
import { QUERY_KEY_FETCH_CUSTOM_AGGREGATE_CATEGORIES, QUERY_KEY_INTENTS_BASE, QUERY_KEY_INTERACTION_AGGREGATES, QUERY_KEY_USER_INTENTS_PAGE__ACTIONS, QUERY_KEY_USER_INTENTS_PAGE__INTENTS, QUERY_KEY_USER_INTENTS_PAGE__TOTALS, QUERY_KEY_USER_QUERIES, QUERY_KEY_USER_TOPIC__TOTALS } from '@/constants/query-keys';
import { ThreeDotsMenu } from '@/comps/component-library/list/ThreeDotsMenu';
import { calcPercentageDiff } from '@/utils/numbers.util';
import { cn } from '@/utils/shad';
import { useAliasedKindName } from '@/contexts/alias';
import { translate } from '@/utils/translations';
import { formatDuration, formatNumber, formatPercentage } from '@/utils/format.util';
import { PlusCircledIcon } from '@radix-ui/react-icons';
import { useQueryClient } from '@tanstack/react-query';
import { capitalize, isNumber } from 'lodash';
import { Angry, ArrowLeft, ArrowRightLeft, FlaskConical, FolderOpen, Frown, GitMerge, Lightbulb, ListChecks, Loader, LucideIcon, Meh, Pencil, PieChart, Smile, SmilePlus, Trash, TrendingDown, TrendingUp, X, XCircle } from 'lucide-react';
import React, { FC, PropsWithChildren, ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { NavElement } from '../_components/nav-button';
import { useDeleteCategory, useMoveCategories, useMoveInteractions } from '@/api/generated/categories/categories';
import { useInvalidateCache } from '@/api/generated/projects/projects';
import { useMapCategories, useMapIntents, useMapTopics, useUnmapIntent, useUnmapTopic } from '@/api/generated/value-mappings/value-mappings';
import { useGlobalFiltersContext, useQueryFilters } from '@/contexts/filters/filter-context';
import { useAuthContext } from '@/hooks/use-auth';
import { FilterTypeOfProblemV2, GenericFilter, GetInteractionAggregatesReq, TimeRangeConfig } from '@/api/generated/model';
import { useDebounceValue } from '@/hooks/use-debounce';
import { useFetchCustomAggregateCategories, useFetchInteractionAggregates } from '@/models/interaction-aggregates/useFetchInteractionAggregates';
import { useHasCategoryEnabled } from '@/domains/home/hooks/useGroupByItems';
import { useProjectsContext } from '@/providers/projects-provider';
import { SearchInputV2 } from '@/comps/search-input';
import { CategoryKindsSelector } from '@/domains/user-intelligence/groupby/components/customEntitties/CategoryKindsSelector';
import { WithEmptyState } from '@/comps/empty-state';
import { Pagination } from '@/comps/pagination';
import { PARAMS_KEY_LLM_PROBLEMS_PROBLEM_FILTERS, PARAMS_KEY_INTENTS_TABLE_GROUPBY, PARAMS_KEY_USER_INTENTS_FILTERS, PARAMS_KEY_USER_INTENTS_VERTICAL_TAB_STRING } from '@/constants/route-params-keys';
import { useQueryParamStateV2 } from '@/utils/use-query-param-state';
import { CreateAggregateCategoryPortalButton, CreateCategoryDialogSection } from '@/domains/user-intelligence/groupby/components/customEntitties/CreateAggregateCategoryButton';
import { useJobToast } from '@/components/async-job-toast';
import { toast } from 'sonner';
import { WithClassName } from '@/types/component';
import { onEnterPress } from '@/utils/keyboard.utils';
import { RenameCategoryDrawer } from '@/domains/live-activity/interactions/components/RenameCategoryDialog';
import { useAvailableLabelNamesByScope, useBulkConversationLabels, useBulkInteractionLabels } from '@/hooks/interactions/use-interaction-labels';
import { useConfig } from '@/hooks/use-config';
import { fallbackQueryAxisForKind, getDestinationStepLabel, mapAggregateNodeToQueryAxis, resolveActionMoveParentKinds, resolveAggregateMovePath, filterCustomCategoriesByAncestorPath, resolveMoveFromParentName } from './move-to-dialog.utils';
export const colorsWithAlpha = ['#D6EC4C66', '#4C96EC66', '#EC4C7266', '#58B2CB66', '#6AB58666', '#A92AE566'];
export const colors = ['#D6EC4C', '#4C96EC', '#EC4C72', '#58B2CB', '#6AB586', '#a921e5'];
export const borderStyles = [`border pl-2 py-[2px] rounded-[20px] border-[${colorsWithAlpha[0]}]`, `border pl-2 py-0.5 rounded-[20px] border-[${colorsWithAlpha[1]}]`, `border pl-2 py-0.5 rounded-[20px] border-[${colorsWithAlpha[2]}]`, `border pl-2 py-0.5 rounded-[20px] border-[${colorsWithAlpha[3]}]`, `border pl-2 py-0.5 rounded-[20px] border-[${colorsWithAlpha[4]}]`, `border pl-2 py-0.5 rounded-[20px] border-[${colorsWithAlpha[5]}]`];
export const bgColors = [`bg-[${colors[0]}]`, `bg-[${colors[1]}]`, `bg-[${colors[2]}]`, `bg-[${colors[3]}]`, `bg-[${colors[4]}]`, `bg-[${colors[5]}]`];
export type SentimentType = 'Neutral' | 'Positive' | 'Negative' | 'Very positive' | 'Very negative' | 'Mixed' | 'None';
export type EmotionType = 'Neutral' | 'Admiration' | 'Relief' | 'Amusement' | 'Gratitude' | 'Satisfaction' | 'Interest' | 'Thankfulness' | 'Trust' | 'Surprise' | 'Appreciation' | 'Joy' | 'None' | 'Criticism' | 'Disapproval' | 'Frustration' | 'Fear' | 'Anger' | 'Hate' | 'Violence' | 'Sexual Desire' | 'Love' | 'Confusion' | 'Annoyance' | 'Disappointment' | 'Disgust' | 'Tension' | 'Regret' | 'Concern' | 'Sadness' | 'Uncertainty' | 'Disinterest' | 'Anxiety' | 'Apprehension' | 'Skepticism';
export const emotionsHex: Record<EmotionType, string> = {
  Neutral: '#718DBC',
  Admiration: '#99D6DA',
  Relief: '#6BBBD2',
  Amusement: '#2ED9FF',
  Gratitude: '#449B5C',
  Satisfaction: '#428E2F',
  Interest: '#0EFBA6',
  Thankfulness: '#3986E1',
  Trust: '#2963F7',
  Surprise: '#D6EC4C',
  Appreciation: '#EFC83F',
  Joy: '#FF9900',
  None: '#B2B2B2',
  Criticism: '#6B518C',
  Disapproval: '#7410F5',
  Frustration: '#BD2E73',
  Fear: '#BC39BF',
  Anger: '#F73939',
  Hate: '#A81C1C',
  Violence: '#F40B27',
  'Sexual Desire': '#DA615C',
  Love: '#F64F95',
  Confusion: '#727698',
  Annoyance: '#B8BFAA',
  Disappointment: '#4D50A6',
  Disgust: '#4804A1',
  Tension: '#8F29CE',
  Regret: '#9465B0',
  Concern: '#718DBC',
  Sadness: '#396370',
  Uncertainty: '#396370',
  Disinterest: '#71717A',
  Anxiety: '#9DFF20',
  Apprehension: '#6F852F',
  Skepticism: '#6C7A5A'
};
export const emotionsColorDimm: Record<EmotionType, string> = {
  Neutral: `bg-[${emotionsHex.Neutral}]/15`,
  Admiration: `bg-[${emotionsHex.Admiration}]/15`,
  Relief: `bg-[${emotionsHex.Relief}]/15`,
  Amusement: `bg-[${emotionsHex.Amusement}]/15`,
  Gratitude: `bg-[${emotionsHex.Gratitude}]/15`,
  Satisfaction: `bg-[${emotionsHex.Satisfaction}]/15`,
  Interest: `bg-[${emotionsHex.Interest}]/15`,
  Thankfulness: `bg-[${emotionsHex.Thankfulness}]/15`,
  Trust: `bg-[${emotionsHex.Trust}]/15`,
  Surprise: `bg-[${emotionsHex.Surprise}]/15`,
  Appreciation: `bg-[${emotionsHex.Appreciation}]/15`,
  Joy: `bg-[${emotionsHex.Joy}]/15`,
  None: `bg-[${emotionsHex.None}]/15`,
  Criticism: `bg-[${emotionsHex.Criticism}]/15`,
  Disapproval: `bg-[${emotionsHex.Disapproval}]/15`,
  Frustration: `bg-[${emotionsHex.Frustration}]/15`,
  Fear: `bg-[${emotionsHex.Fear}]/15`,
  Anger: `bg-[${emotionsHex.Anger}]/15`,
  Hate: `bg-[${emotionsHex.Hate}]/15`,
  Violence: `bg-[${emotionsHex.Violence}]/15`,
  'Sexual Desire': `bg-[${emotionsHex['Sexual Desire']}]/15`,
  Love: `bg-[${emotionsHex.Love}]/15`,
  Confusion: `bg-[${emotionsHex.Confusion}]/15`,
  Annoyance: `bg-[${emotionsHex.Annoyance}]/15`,
  Disappointment: `bg-[${emotionsHex.Disappointment}]/15`,
  Disgust: `bg-[${emotionsHex.Disgust}]/15`,
  Tension: `bg-[${emotionsHex.Tension}]/15`,
  Regret: `bg-[${emotionsHex.Regret}]/15`,
  Concern: `bg-[${emotionsHex.Concern}]/15`,
  Sadness: `bg-[${emotionsHex.Sadness}]/15`,
  Uncertainty: `bg-[${emotionsHex.Uncertainty}]/15`,
  Disinterest: `bg-[${emotionsHex.Disinterest}]/15`,
  Anxiety: `bg-[${emotionsHex.Anxiety}]/15`,
  Apprehension: `bg-[${emotionsHex.Apprehension}]/15`,
  Skepticism: `bg-[${emotionsHex.Skepticism}]/15`
};
export const emotionsColor: Record<EmotionType, string> = {
  Neutral: `bg-[${emotionsHex.Neutral}]`,
  Admiration: `bg-[${emotionsHex.Admiration}]`,
  Relief: `bg-[${emotionsHex.Relief}]`,
  Amusement: `bg-[${emotionsHex.Amusement}]`,
  Gratitude: `bg-[${emotionsHex.Gratitude}]`,
  Satisfaction: `bg-[${emotionsHex.Satisfaction}]`,
  Interest: `bg-[${emotionsHex.Interest}]`,
  Thankfulness: `bg-[${emotionsHex.Thankfulness}]`,
  Trust: `bg-[${emotionsHex.Trust}]`,
  Surprise: `bg-[${emotionsHex.Surprise}]`,
  Appreciation: `bg-[${emotionsHex.Appreciation}]`,
  Joy: `bg-[${emotionsHex.Joy}]`,
  None: `bg-[${emotionsHex.None}]`,
  Criticism: `bg-[${emotionsHex.Criticism}]`,
  Disapproval: `bg-[${emotionsHex.Disapproval}]`,
  Frustration: `bg-[${emotionsHex.Frustration}]`,
  Fear: `bg-[${emotionsHex.Fear}]`,
  Anger: `bg-[${emotionsHex.Anger}]`,
  Hate: `bg-[${emotionsHex.Hate}]`,
  Violence: `bg-[${emotionsHex.Violence}]`,
  'Sexual Desire': `bg-[${emotionsHex['Sexual Desire']}]`,
  Love: `bg-[${emotionsHex.Love}]`,
  Confusion: `bg-[${emotionsHex.Confusion}]`,
  Annoyance: `bg-[${emotionsHex.Annoyance}]`,
  Disappointment: `bg-[${emotionsHex.Disappointment}]`,
  Disgust: `bg-[${emotionsHex.Disgust}]`,
  Tension: `bg-[${emotionsHex.Tension}]`,
  Regret: `bg-[${emotionsHex.Regret}]`,
  Concern: `bg-[${emotionsHex.Concern}]`,
  Sadness: `bg-[${emotionsHex.Sadness}]`,
  Uncertainty: `bg-[${emotionsHex.Uncertainty}]`,
  Disinterest: `bg-[${emotionsHex.Disinterest}]`,
  Anxiety: `bg-[${emotionsHex.Anxiety}]`,
  Apprehension: `bg-[${emotionsHex.Apprehension}]`,
  Skepticism: `bg-[${emotionsHex.Skepticism}]`
};
export const sentimentHex: Record<SentimentType, string> = {
  Neutral: '#718DBC',
  Positive: '#5EFCE8',
  Negative: '#924CEC',
  'Very positive': '#58CD83',
  'Very negative': '#EC4C72',
  Mixed: '#FFA500',
  None: '#FFFFFF'
};
export const sentimentColors: Record<SentimentType, string> = {
  Neutral: `bg-[${sentimentHex.Neutral}66]`,
  Positive: `bg-[${sentimentHex.Positive}66]`,
  Negative: `bg-[${sentimentHex.Negative}66]`,
  'Very positive': `bg-[${sentimentHex['Very positive']}66]`,
  'Very negative': `bg-[${sentimentHex['Very negative']}66]`,
  Mixed: `bg-[${sentimentHex.Mixed}66]`,
  None: `bg-[${sentimentHex.None}66]`
};
export const sentimentText: Record<SentimentType, string> = {
  Neutral: `text-[${sentimentHex.Neutral}66]`,
  Positive: `text-[${sentimentHex.Positive}66]`,
  Negative: `text-[${sentimentHex.Negative}66]`,
  'Very positive': `text-[${sentimentHex['Very positive']}66]`,
  'Very negative': `text-[${sentimentHex['Very negative']}66]`,
  Mixed: `text-[${sentimentHex.Mixed}66]`,
  None: `text-[${sentimentHex.None}66]`
};
export const sentimentIcons: Record<SentimentType, ReactElement> = {
  Neutral: <Meh size={12} />,
  Positive: <Smile size={12} />,
  Negative: <Frown size={12} />,
  'Very positive': <SmilePlus size={12} />,
  'Very negative': <Angry size={12} />,
  Mixed: <Meh size={12} />,
  None: <Meh size={12} />
};
export const sentimentBorders: Record<SentimentType, string> = {
  Neutral: `border-[${sentimentHex.Neutral}66]`,
  Positive: `border-[${sentimentHex.Positive}66]`,
  Negative: `border-[${sentimentHex.Negative}66]`,
  'Very positive': `border-[${sentimentHex['Very positive']}66]`,
  'Very negative': `border-[${sentimentHex['Very negative']}66]`,
  Mixed: `border-[${sentimentHex.Mixed}66]`,
  None: `border-[${sentimentHex.None}66]`
};
export const sentimentsEmotionsHexMap = (title: string, groupBy?: string) => {
  if (groupBy === 'topic') {
    return colors[0];
  } else if (groupBy === 'sentiment') {
    return sentimentHex[title as SentimentType];
  } else if (groupBy === 'emotions') {
    return emotionsHex[title as EmotionType];
  } else return undefined;
};
export const textMap = (title: string, groupBy?: string) => {
  if (groupBy === 'topic') {
    return 'text-n-violet-platform';
  } else if (groupBy === 'sentiment') {
    return sentimentText[title as SentimentType];
  } else if (groupBy === 'emotions') {
    return emotionsColor[title as EmotionType];
  } else return undefined;
};
export const colorsMap = (title: string, groupBy?: string) => {
  if (groupBy === 'topic') {
    return 'bg-n-violet-platform';
  } else if (groupBy === 'sentiment') {
    return sentimentColors[title as SentimentType];
  } else if (groupBy === 'emotions') {
    return emotionsColorDimm[title as EmotionType];
  } else return undefined;
};
export const bordersMap = (title: string, groupBy?: string) => {
  if (groupBy === 'topic') {
    return 'border-n-violet-platform';
  } else if (groupBy === 'sentiment') {
    return sentimentBorders[title as SentimentType];
  } else return undefined;
};
export type RowKind = 'topic' | 'user_action' | 'custom';
export const availableSentiments = ['neutral', 'mixed', 'very_positive', 'very_negative', 'positive', 'negative'];

// export const IntentChip = ({
//   metricName,
//   metric,
//   delta,
//   invert,
// }: {
//   metricName: 'users' | 'interactions';
//   metric: number;
//   delta?: number;
//   invert?: boolean;
// }) => {
//   const Icon = metricName === 'users' ? Users2Icon : Activity;
//
//   return (
//     <div className="py-0">
//       <Chip
//         variant="muted"
//         size="small"
//         className="flex w-fit items-center gap-1 text-nowrap rounded-[6px] px-1.25 py-px text-[12px] text-muted-foreground"
//       >
//         <Icon className="h-[12px] w-[12px]" />
//         <span className="text-nowrap">
//           {metric}&nbsp;{capitalize(metricName)}
//         </span>
//         {delta !== undefined && <TrendingSubChip delta={delta} invert={invert} />}
//       </Chip>
//     </div>
//   );
// };
//
// export const IntentsSortSection = ({
//   nIntents,
//   category,
//   onChangeSortBy,
//   sortBy = 'users',
// }: {
//   nIntents: number;
//   category: string;
//   onChangeSortBy?: (sortBy: IntentListSortBy) => void;
//   sortBy?: IntentListSortBy;
// }) => {
//   const inactiveClasses = 'text-muted-foreground';
//
//   return (
//     <div className="m b-5 mt-6 flex items-center justify-between">
//       <div className="mb-5 flex items-center text-muted-foreground">
//         <p>{nIntents} User intents found for: </p>
//         <Chip variant="muted" className="ml-3">
//           {category}
//         </Chip>
//       </div>
//
//       <div className="flex items-center gap-3">
//         <p className="text-link">Sort by</p>
//
//         <div
//           className={cn('flex items-center gap-1 hover:cursor-pointer', sortBy !== 'users' && inactiveClasses)}
//           onClick={() => onChangeSortBy?.('users')}
//         >
//           <User2Icon className="h-4 w-4" />
//           <p>Users</p>
//         </div>
//
//         <div
//           className={cn('flex items-center gap-1 hover:cursor-pointer', sortBy !== 'warnings' && inactiveClasses)}
//           onClick={() => onChangeSortBy?.('warnings')}
//         >
//           <UserFeedbackIconZ className="h-4 w-4" />
//           <p>User feedback</p>
//         </div>
//       </div>
//     </div>
//   );
// };

interface IProps {
  projectId: string;
  intent: string;
  groupBy?: string;
}
export const IntentsTableActions: FC<IProps> = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div onClick={_temp} className="w-fit"><ThreeDotsMenu items={[{
        label: "Delete",
        onClick: _temp2
      }]} /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
export interface TotalIntentAggregates {
  isLoading?: boolean;
  total_interactions?: number;
  total_users?: number;
  total_sessions?: number;
}
export const MergedUserIntentDialog = t0 => {
  const $ = _c(63);
  const {
    children,
    selectedUserIntents,
    projectId,
    onSuccess
  } = t0;
  const [open, setOpen] = React.useState(false);
  const aliasKindName = useAliasedKindName();
  let actionLabel;
  let t1;
  if ($[0] !== aliasKindName) {
    actionLabel = aliasKindName("user_action") ?? translate("filter", "user_action");
    t1 = actionLabel.toLowerCase();
    $[0] = aliasKindName;
    $[1] = actionLabel;
    $[2] = t1;
  } else {
    actionLabel = $[1];
    t1 = $[2];
  }
  const actionLabelLower = t1;
  let t2;
  if ($[3] !== selectedUserIntents[0]) {
    t2 = capitalize(selectedUserIntents[0]) ?? "";
    $[3] = selectedUserIntents[0];
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const [newIntent, setNewIntent] = React.useState(t2);
  const client = useQueryClient();
  let t3;
  if ($[5] !== client || $[6] !== onSuccess) {
    t3 = {
      mutation: {
        onSuccess: () => {
          setOpen(false);
          client.invalidateQueries({
            queryKey: [QUERY_KEY_INTENTS_BASE]
          });
          client.invalidateQueries({
            queryKey: [QUERY_KEY_INTERACTION_AGGREGATES]
          });
          client.invalidateQueries({
            queryKey: [QUERY_KEY_USER_INTENTS_PAGE__ACTIONS]
          });
          client.invalidateQueries({
            queryKey: ["interactionAggregatesv2"]
          });
          onSuccess();
        }
      }
    };
    $[5] = client;
    $[6] = onSuccess;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const {
    mutate,
    isLoading
  } = useMapIntents(t3);
  let t4;
  if ($[8] !== children) {
    t4 = <DialogTrigger asChild={true}>{children}</DialogTrigger>;
    $[8] = children;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== isLoading) {
    t5 = isLoading && <LoadingSpinner fullContainer={true} />;
    $[10] = isLoading;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const t6 = `New merged ${actionLabelLower}`;
  let t7;
  if ($[12] !== t6) {
    t7 = <NavElement Icon={FlaskConical} text={t6} coloredIcon={true} isActive={true} showTooltip={false} />;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <X size={20} onClick={() => setOpen(false)} className="text-muted-foreground hover:cursor-pointer" />;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t7) {
    t9 = <div className="flex items-center justify-between border-b px-7">{t7}{t8}</div>;
    $[15] = t7;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const t10 = `Create a new ${actionLabelLower} by merging existing ones`;
  let t11;
  if ($[17] !== t10) {
    t11 = <p className="text-muted-foreground">{t10}</p>;
    $[17] = t10;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const t12 = `Remember: The new ${actionLabelLower} will inherit the users, interactions and the user feedback of the previous ones.`;
  let t13;
  if ($[19] !== t12) {
    t13 = <p className="text-darkgrey">{t12}</p>;
    $[19] = t12;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== t11 || $[22] !== t13) {
    t14 = <div className="px-8 py-3">{t11}{t13}</div>;
    $[21] = t11;
    $[22] = t13;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <Lightbulb className="h-4 w-4" />;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  const t16 = `Selected ${actionLabel}s`;
  let t17;
  if ($[25] !== t16) {
    t17 = <div className="flex h-12 items-center gap-1 border-y px-8 text-muted-foreground">{t15}<p>{t16}</p></div>;
    $[25] = t16;
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  let t18;
  if ($[27] !== selectedUserIntents) {
    t18 = selectedUserIntents.map(_temp3);
    $[27] = selectedUserIntents;
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  let t19;
  if ($[29] !== t18) {
    t19 = <div className="flex flex-col px-8 py-3">{t18}</div>;
    $[29] = t18;
    $[30] = t19;
  } else {
    t19 = $[30];
  }
  let t20;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <Pencil className="h-4 w-4" />;
    $[31] = t20;
  } else {
    t20 = $[31];
  }
  const t21 = `Name of the new ${actionLabelLower}`;
  let t22;
  if ($[32] !== t21) {
    t22 = <div className="flex h-12 items-center gap-1 border-y px-8 text-muted-foreground">{t20}<p>{t21}</p></div>;
    $[32] = t21;
    $[33] = t22;
  } else {
    t22 = $[33];
  }
  const t23 = `${actionLabel} name`;
  let t24;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = e_0 => setNewIntent(e_0.currentTarget.value);
    $[34] = t24;
  } else {
    t24 = $[34];
  }
  let t25;
  if ($[35] !== newIntent || $[36] !== t23) {
    t25 = <div className="border-b px-8 py-3"><Input placeholder={t23} value={newIntent} onChange={t24} /></div>;
    $[35] = newIntent;
    $[36] = t23;
    $[37] = t25;
  } else {
    t25 = $[37];
  }
  const t26 = !newIntent || isLoading;
  let t27;
  if ($[38] !== mutate || $[39] !== newIntent || $[40] !== projectId || $[41] !== selectedUserIntents) {
    t27 = () => mutate({
      data: {
        project_id: projectId,
        mapped_value: newIntent,
        original_values: selectedUserIntents
      }
    });
    $[38] = mutate;
    $[39] = newIntent;
    $[40] = projectId;
    $[41] = selectedUserIntents;
    $[42] = t27;
  } else {
    t27 = $[42];
  }
  let t28;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <PlusCircledIcon className="h-4 w-4" />;
    $[43] = t28;
  } else {
    t28 = $[43];
  }
  const t29 = `New ${actionLabelLower}`;
  let t30;
  if ($[44] !== t29) {
    t30 = <p>{t29}</p>;
    $[44] = t29;
    $[45] = t30;
  } else {
    t30 = $[45];
  }
  let t31;
  if ($[46] !== t26 || $[47] !== t27 || $[48] !== t30) {
    t31 = <div className="flex justify-end px-8 py-4"><Button disabled={t26} variant="purple" className="gap flex items-center px-1.75!" onClick={t27}>{t28}{t30}</Button></div>;
    $[46] = t26;
    $[47] = t27;
    $[48] = t30;
    $[49] = t31;
  } else {
    t31 = $[49];
  }
  let t32;
  if ($[50] !== t14 || $[51] !== t17 || $[52] !== t19 || $[53] !== t22 || $[54] !== t25 || $[55] !== t31 || $[56] !== t5 || $[57] !== t9) {
    t32 = <DialogContentWrapper className="p-0"><div className="relative">{t5}{t9}{t14}{t17}{t19}{t22}{t25}{t31}</div></DialogContentWrapper>;
    $[50] = t14;
    $[51] = t17;
    $[52] = t19;
    $[53] = t22;
    $[54] = t25;
    $[55] = t31;
    $[56] = t5;
    $[57] = t9;
    $[58] = t32;
  } else {
    t32 = $[58];
  }
  let t33;
  if ($[59] !== open || $[60] !== t32 || $[61] !== t4) {
    t33 = <Dialog open={open} onOpenChange={setOpen}>{t4}{t32}</Dialog>;
    $[59] = open;
    $[60] = t32;
    $[61] = t4;
    $[62] = t33;
  } else {
    t33 = $[62];
  }
  return t33;
};
export const MergedTopicDialog = t0 => {
  const $ = _c(63);
  const {
    children,
    selectedUserIntents,
    projectId,
    onSuccess
  } = t0;
  const [open, setOpen] = React.useState(false);
  const aliasKindName = useAliasedKindName();
  let t1;
  let topicLabel;
  if ($[0] !== aliasKindName) {
    topicLabel = aliasKindName("topic") ?? translate("filter", "topic");
    t1 = topicLabel.toLowerCase();
    $[0] = aliasKindName;
    $[1] = t1;
    $[2] = topicLabel;
  } else {
    t1 = $[1];
    topicLabel = $[2];
  }
  const topicLabelLower = t1;
  const client = useQueryClient();
  let t2;
  if ($[3] !== selectedUserIntents[0]) {
    t2 = capitalize(selectedUserIntents[0]) ?? "";
    $[3] = selectedUserIntents[0];
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const [newTopic, setNewTopic] = React.useState(t2);
  let t3;
  if ($[5] !== client || $[6] !== onSuccess) {
    t3 = {
      mutation: {
        onSuccess: () => {
          setOpen(false);
          client.invalidateQueries({
            queryKey: [QUERY_KEY_INTERACTION_AGGREGATES]
          });
          client.invalidateQueries({
            queryKey: [QUERY_KEY_USER_INTENTS_PAGE__ACTIONS]
          });
          onSuccess();
        }
      }
    };
    $[5] = client;
    $[6] = onSuccess;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const {
    mutate,
    isLoading
  } = useMapTopics(t3);
  let t4;
  if ($[8] !== children) {
    t4 = <DialogTrigger asChild={true}>{children}</DialogTrigger>;
    $[8] = children;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== isLoading) {
    t5 = isLoading && <LoadingSpinner fullContainer={true} />;
    $[10] = isLoading;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const t6 = `New merged ${topicLabelLower}`;
  let t7;
  if ($[12] !== t6) {
    t7 = <NavElement Icon={FlaskConical} text={t6} coloredIcon={true} isActive={true} showTooltip={false} />;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <X size={20} onClick={() => setOpen(false)} className="text-muted-foreground hover:cursor-pointer" />;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t7) {
    t9 = <div className="flex items-center justify-between border-b px-7">{t7}{t8}</div>;
    $[15] = t7;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const t10 = `Create a new ${topicLabelLower} by merging existing ones`;
  let t11;
  if ($[17] !== t10) {
    t11 = <p className="text-muted-foreground">{t10}</p>;
    $[17] = t10;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const t12 = `Remember: The new ${topicLabelLower} will inherit the users, interactions and the user feedback of the previous ones.`;
  let t13;
  if ($[19] !== t12) {
    t13 = <p className="text-darkgrey">{t12}</p>;
    $[19] = t12;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== t11 || $[22] !== t13) {
    t14 = <div className="px-8 py-3">{t11}{t13}</div>;
    $[21] = t11;
    $[22] = t13;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <Lightbulb className="h-4 w-4" />;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  const t16 = `Selected ${topicLabel}s`;
  let t17;
  if ($[25] !== t16) {
    t17 = <div className="flex h-12 items-center gap-1 border-y px-8 text-muted-foreground">{t15}<p>{t16}</p></div>;
    $[25] = t16;
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  let t18;
  if ($[27] !== selectedUserIntents) {
    t18 = selectedUserIntents.map(_temp4);
    $[27] = selectedUserIntents;
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  let t19;
  if ($[29] !== t18) {
    t19 = <div className="flex flex-col px-8 py-3">{t18}</div>;
    $[29] = t18;
    $[30] = t19;
  } else {
    t19 = $[30];
  }
  let t20;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <Pencil className="h-4 w-4" />;
    $[31] = t20;
  } else {
    t20 = $[31];
  }
  const t21 = `Name of the new ${topicLabelLower}`;
  let t22;
  if ($[32] !== t21) {
    t22 = <div className="flex h-12 items-center gap-1 border-y px-8 text-muted-foreground">{t20}<p>{t21}</p></div>;
    $[32] = t21;
    $[33] = t22;
  } else {
    t22 = $[33];
  }
  const t23 = `${topicLabel} name`;
  let t24;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = e_0 => setNewTopic(e_0.currentTarget.value);
    $[34] = t24;
  } else {
    t24 = $[34];
  }
  let t25;
  if ($[35] !== newTopic || $[36] !== t23) {
    t25 = <div className="border-b px-8 py-3"><Input placeholder={t23} value={newTopic} onChange={t24} /></div>;
    $[35] = newTopic;
    $[36] = t23;
    $[37] = t25;
  } else {
    t25 = $[37];
  }
  const t26 = !newTopic || isLoading;
  let t27;
  if ($[38] !== mutate || $[39] !== newTopic || $[40] !== projectId || $[41] !== selectedUserIntents) {
    t27 = () => mutate({
      data: {
        project_id: projectId,
        mapped_value: newTopic,
        original_values: selectedUserIntents
      }
    });
    $[38] = mutate;
    $[39] = newTopic;
    $[40] = projectId;
    $[41] = selectedUserIntents;
    $[42] = t27;
  } else {
    t27 = $[42];
  }
  let t28;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <PlusCircledIcon className="h-4 w-4" />;
    $[43] = t28;
  } else {
    t28 = $[43];
  }
  const t29 = `New ${topicLabelLower}`;
  let t30;
  if ($[44] !== t29) {
    t30 = <p>{t29}</p>;
    $[44] = t29;
    $[45] = t30;
  } else {
    t30 = $[45];
  }
  let t31;
  if ($[46] !== t26 || $[47] !== t27 || $[48] !== t30) {
    t31 = <div className="flex justify-end px-8 py-4"><Button disabled={t26} variant="purple" className="gap flex items-center px-1.75!" onClick={t27}>{t28}{t30}</Button></div>;
    $[46] = t26;
    $[47] = t27;
    $[48] = t30;
    $[49] = t31;
  } else {
    t31 = $[49];
  }
  let t32;
  if ($[50] !== t14 || $[51] !== t17 || $[52] !== t19 || $[53] !== t22 || $[54] !== t25 || $[55] !== t31 || $[56] !== t5 || $[57] !== t9) {
    t32 = <DialogContentWrapper className="p-0"><div className="relative">{t5}{t9}{t14}{t17}{t19}{t22}{t25}{t31}</div></DialogContentWrapper>;
    $[50] = t14;
    $[51] = t17;
    $[52] = t19;
    $[53] = t22;
    $[54] = t25;
    $[55] = t31;
    $[56] = t5;
    $[57] = t9;
    $[58] = t32;
  } else {
    t32 = $[58];
  }
  let t33;
  if ($[59] !== open || $[60] !== t32 || $[61] !== t4) {
    t33 = <Dialog open={open} onOpenChange={setOpen}>{t4}{t32}</Dialog>;
    $[59] = open;
    $[60] = t32;
    $[61] = t4;
    $[62] = t33;
  } else {
    t33 = $[62];
  }
  return t33;
};
export const MergedCategoryDialog = t0 => {
  const $ = _c(40);
  const {
    children,
    selectedCategories,
    projectId,
    onSuccess
  } = t0;
  const [open, setOpen] = React.useState(false);
  const client = useQueryClient();
  let t1;
  if ($[0] !== selectedCategories[0]) {
    t1 = capitalize(selectedCategories[0]) ?? "";
    $[0] = selectedCategories[0];
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [newCategory, setNewCategory] = React.useState(t1);
  let t2;
  if ($[2] !== client || $[3] !== onSuccess) {
    t2 = {
      mutation: {
        onSuccess: () => {
          setOpen(false);
          client.invalidateQueries({
            queryKey: [QUERY_KEY_FETCH_CUSTOM_AGGREGATE_CATEGORIES]
          });
          client.invalidateQueries({
            queryKey: [QUERY_KEY_INTERACTION_AGGREGATES]
          });
          client.invalidateQueries({
            queryKey: [QUERY_KEY_USER_INTENTS_PAGE__ACTIONS]
          });
          client.invalidateQueries({
            queryKey: [QUERY_KEY_USER_INTENTS_PAGE__INTENTS]
          });
          onSuccess();
        }
      }
    };
    $[2] = client;
    $[3] = onSuccess;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const {
    mutate,
    isLoading
  } = useMapCategories(t2);
  let t3;
  if ($[5] !== children) {
    t3 = <DialogTrigger asChild={true}>{children}</DialogTrigger>;
    $[5] = children;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== isLoading) {
    t4 = isLoading && <LoadingSpinner fullContainer={true} />;
    $[7] = isLoading;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <NavElement Icon={FlaskConical} text="New merged category" coloredIcon={true} isActive={true} showTooltip={false} />;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="flex items-center justify-between border-b px-7">{t5}<X size={20} onClick={() => setOpen(false)} className="text-muted-foreground hover:cursor-pointer" /></div>;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="px-8 py-3"><p className="text-muted-foreground">Create a new category by merging existing ones</p><p className="text-darkgrey">Remember: The new category will inherit the users, interactions and the user feedback of the previous ones.</p></div>;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="flex h-12 items-center gap-1 border-y px-8 text-muted-foreground"><Lightbulb className="h-4 w-4" /><p>Selected Categories</p></div>;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== selectedCategories) {
    t9 = selectedCategories.map(_temp5);
    $[13] = selectedCategories;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t9) {
    t10 = <div className="flex flex-col px-8 py-3">{t9}</div>;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <div className="flex h-12 items-center gap-1 border-y px-8 text-muted-foreground"><Pencil className="h-4 w-4" /><p>Name of the new category</p></div>;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = e_0 => setNewCategory(e_0.currentTarget.value);
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== newCategory) {
    t13 = <div className="border-b px-8 py-3"><Input placeholder="Category name" value={newCategory} onChange={t12} /></div>;
    $[19] = newCategory;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  const t14 = !newCategory || isLoading;
  let t15;
  if ($[21] !== mutate || $[22] !== newCategory || $[23] !== projectId || $[24] !== selectedCategories) {
    t15 = () => mutate({
      data: {
        project_id: projectId,
        mapped_value: newCategory,
        original_values: selectedCategories
      }
    });
    $[21] = mutate;
    $[22] = newCategory;
    $[23] = projectId;
    $[24] = selectedCategories;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  let t17;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <PlusCircledIcon className="h-4 w-4" />;
    t17 = <p>New category</p>;
    $[26] = t16;
    $[27] = t17;
  } else {
    t16 = $[26];
    t17 = $[27];
  }
  let t18;
  if ($[28] !== t14 || $[29] !== t15) {
    t18 = <div className="flex justify-end px-8 py-4"><Button disabled={t14} variant="purple" className="gap flex items-center px-1.75!" onClick={t15}>{t16}{t17}</Button></div>;
    $[28] = t14;
    $[29] = t15;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  let t19;
  if ($[31] !== t10 || $[32] !== t13 || $[33] !== t18 || $[34] !== t4) {
    t19 = <DialogContentWrapper className="p-0"><div className="relative">{t4}{t6}{t7}{t8}{t10}{t11}{t13}{t18}</div></DialogContentWrapper>;
    $[31] = t10;
    $[32] = t13;
    $[33] = t18;
    $[34] = t4;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] !== open || $[37] !== t19 || $[38] !== t3) {
    t20 = <Dialog open={open} onOpenChange={setOpen}>{t3}{t19}</Dialog>;
    $[36] = open;
    $[37] = t19;
    $[38] = t3;
    $[39] = t20;
  } else {
    t20 = $[39];
  }
  return t20;
};
export const LoadingChip = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Chip variant="muted" className="flex w-fit items-center gap-1 px-1.5 py-1 text-[12px] leading-4 text-foreground"><Loader size={12} /><span>Analysing ~2hrs...</span></Chip>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
const trendingColors = ['text-positivev2', 'text-negativev2'];
export const SentimentMessageChip = props => {
  const $ = _c(19);
  let t0;
  if ($[0] !== props.totalIconTooltip || $[1] !== props.totalPercentage) {
    t0 = props.totalPercentage !== undefined && <TooltipButton enabled={true} content={props.totalIconTooltip}><CircularPercentage percentage={props.totalPercentage} className="text-[12px] text-nowrap text-white" /></TooltipButton>;
    $[0] = props.totalIconTooltip;
    $[1] = props.totalPercentage;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== props.Icon || $[4] !== props.iconClass) {
    t1 = props.Icon && <props.Icon className={cn("mr-0.5 h-3 w-3", props.iconClass)} size={12} />;
    $[3] = props.Icon;
    $[4] = props.iconClass;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== props.isPercentage || $[7] !== props.isTimeDelta || $[8] !== props.value) {
    t2 = props.isTimeDelta ? props.value === undefined ? null : formatDuration(props.value, {
      inputUnit: "seconds",
      style: "human"
    }) : props.isPercentage ? formatPercentage(props.value, {
      inputScale: "decimal",
      decimals: 0
    }) : props.value ?? 0;
    $[6] = props.isPercentage;
    $[7] = props.isTimeDelta;
    $[8] = props.value;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== t2) {
    t3 = <span className="text-[12px] text-muted-foreground">{t2}</span>;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <p className="ml-1 text-muted-foreground">messages</p>;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== t1 || $[14] !== t3) {
    t5 = <div className="flex items-center text-accent-foreground">{t1}{t3}{t4}</div>;
    $[13] = t1;
    $[14] = t3;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== t0 || $[17] !== t5) {
    t6 = <div className="flex items-center gap-1.5">{t0}{t5}</div>;
    $[16] = t0;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  return t6;
};
export const StatComp = props => {
  const $ = _c(35);
  let t0;
  if ($[0] !== props.previousDelta || $[1] !== props.previousValue || $[2] !== props.value) {
    t0 = isNumber(props.previousDelta) ? props.previousDelta : calcPercentageDiff(props.value, props.previousValue);
    $[0] = props.previousDelta;
    $[1] = props.previousValue;
    $[2] = props.value;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const diff = t0;
  const {
    aggregateBy
  } = useGlobalFiltersContext();
  let t1;
  if ($[4] !== props.Icon || $[5] !== props.iconClass) {
    t1 = props.Icon && <props.Icon className={cn("mr-0.5 h-3 w-3", props.iconClass)} size={12} />;
    $[4] = props.Icon;
    $[5] = props.iconClass;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== props.isPercentage || $[8] !== props.useCirclePercentageComponent || $[9] !== props.value) {
    t2 = props.useCirclePercentageComponent && props.isPercentage && <CircularPercentage percentage={props.value ?? 0} color="red" className="text-[12px] text-nowrap text-white" />;
    $[7] = props.isPercentage;
    $[8] = props.useCirclePercentageComponent;
    $[9] = props.value;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== props.isPercentage || $[12] !== props.isRatio || $[13] !== props.isTimeDelta || $[14] !== props.useCirclePercentageComponent || $[15] !== props.value) {
    t3 = props.isTimeDelta ? props.value === undefined ? null : formatDuration(props.value, {
      inputUnit: "seconds",
      style: "human"
    }) : props.isPercentage ? !props.useCirclePercentageComponent ? formatPercentage(props.value, {
      inputScale: "decimal",
      decimals: 0
    }) : null : props.isRatio ? `${props.value}x` : formatNumber(props.value);
    $[11] = props.isPercentage;
    $[12] = props.isRatio;
    $[13] = props.isTimeDelta;
    $[14] = props.useCirclePercentageComponent;
    $[15] = props.value;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  let t4;
  if ($[17] !== t3) {
    t4 = <span className="text-[12px]">{t3}</span>;
    $[17] = t3;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  let t5;
  if ($[19] !== t1 || $[20] !== t2 || $[21] !== t4) {
    t5 = <div className="flex items-center text-accent-foreground">{t1}{t2}{t4}</div>;
    $[19] = t1;
    $[20] = t2;
    $[21] = t4;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  let t6;
  if ($[23] !== aggregateBy || $[24] !== diff || $[25] !== props.hidePreviousChip) {
    t6 = !aggregateBy.includes("conversation") && !props.hidePreviousChip && <TrendingChip diff={diff ?? undefined} />;
    $[23] = aggregateBy;
    $[24] = diff;
    $[25] = props.hidePreviousChip;
    $[26] = t6;
  } else {
    t6 = $[26];
  }
  let t7;
  if ($[27] !== props.totalIconColor || $[28] !== props.totalIconTooltip || $[29] !== props.totalPercentage) {
    t7 = props.totalPercentage !== undefined && <StatChip Icon={PieChart} iconClassName={props.totalIconColor} text={formatPercentage(props.totalPercentage, {
      inputScale: "decimal",
      decimals: 0
    })} rateOfChangeTooltip={false} pieChartTooltip={true} pieChartTooltipText={props.totalIconTooltip} />;
    $[27] = props.totalIconColor;
    $[28] = props.totalIconTooltip;
    $[29] = props.totalPercentage;
    $[30] = t7;
  } else {
    t7 = $[30];
  }
  let t8;
  if ($[31] !== t5 || $[32] !== t6 || $[33] !== t7) {
    t8 = <div className="flex items-center gap-1.5">{t5}{t6}{t7}</div>;
    $[31] = t5;
    $[32] = t6;
    $[33] = t7;
    $[34] = t8;
  } else {
    t8 = $[34];
  }
  return t8;
};
export const TrendingChip = t0 => {
  const $ = _c(8);
  const {
    diff,
    invertColors,
    labelProps,
    iconSize
  } = t0;
  let t1;
  bb0: {
    if (!diff) {
      t1 = trendingColors[0];
      break bb0;
    }
    if (invertColors) {
      t1 = trendingColors[diff === null || diff >= 0 ? 1 : 0];
      break bb0;
    }
    t1 = trendingColors[diff === null || diff >= 0 ? 0 : 1];
  }
  const selectedColor = t1;
  const t2 = diff === null || (diff ?? 0) >= 0 ? TrendingUp : TrendingDown;
  let t3;
  if ($[0] !== diff) {
    t3 = formatPercentage(diff, {
      inputScale: "decimal",
      decimals: 0
    });
    $[0] = diff;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== iconSize || $[3] !== labelProps || $[4] !== selectedColor || $[5] !== t2 || $[6] !== t3) {
    t4 = <StatChip Icon={t2} iconClassName={selectedColor} text={t3} rateOfChangeTooltip={true} labelProps={labelProps} iconSize={iconSize} />;
    $[2] = iconSize;
    $[3] = labelProps;
    $[4] = selectedColor;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
};
export const StatChip = props => {
  const $ = _c(14);
  const t0 = props.rateOfChangeTooltip || props.pieChartTooltip;
  const t1 = props.rateOfChangeTooltip ? "Rate of change over the previous timespan. Adjust the timespan using the button in the upper right corner of the platform." : props.pieChartTooltip ? props.pieChartTooltipText ?? "Percentage over the total of the column." : "";
  let t2;
  if ($[0] !== props.Icon || $[1] !== props.iconClassName || $[2] !== props.iconSize) {
    t2 = props.Icon && <props.Icon size={props.iconSize ?? 12} className={props.iconClassName} />;
    $[0] = props.Icon;
    $[1] = props.iconClassName;
    $[2] = props.iconSize;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== props.labelProps || $[5] !== props.text) {
    t3 = <span {...props.labelProps}>{props.text}</span>;
    $[4] = props.labelProps;
    $[5] = props.text;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t2 || $[8] !== t3) {
    t4 = <div className="flex items-center gap-0.75 rounded-base border px-1 py-0.75 text-[10px] leading-3 text-muted-foreground">{t2}{t3}</div>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t0 || $[11] !== t1 || $[12] !== t4) {
    t5 = <TooltipButton enabled={t0} content={t1}>{t4}</TooltipButton>;
    $[10] = t0;
    $[11] = t1;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
};
type SubtopicsCtaProps = {
  selectedTopics: string[];
  projectId: string;
  setSelectedTopics: (s: string[]) => void;
};
export const SubtopicsCta = t0 => {
  const $ = _c(18);
  const {
    selectedTopics,
    setSelectedTopics,
    projectId
  } = t0;
  const {
    spawnToast
  } = useJobToast();
  const client = useQueryClient();
  const {
    isCompanyAdmin
  } = useAuthContext();
  const {
    mutateAsync: deleteCustomAggregateCategory
  } = useDeleteCategory();
  if (!isCompanyAdmin) {
    return;
  }
  let t1;
  if ($[0] !== selectedTopics.length) {
    t1 = <div className="mr-2 rounded-[5px] border border-accent-foreground px-1">+{selectedTopics.length}</div>;
    $[0] = selectedTopics.length;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span className="mr-2">selected</span>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== client || $[4] !== deleteCustomAggregateCategory || $[5] !== projectId || $[6] !== selectedTopics || $[7] !== setSelectedTopics || $[8] !== spawnToast) {
    t3 = selectedTopics.length > 0 && <><RenameCategoryDrawer kind="category" oldName={selectedTopics[0]} onRename={() => setSelectedTopics([])}><Button disabled={selectedTopics.length !== 1} size="sm" className="flex items-center rounded-base border border-accent-foreground text-[12px] hover:bg-transparent" variant="ghost"><Pencil size={12} className="mr-2" /><span>Rename</span></Button></RenameCategoryDrawer><span className="mr-1">or</span><MoveToDialog projectId={projectId} selectedItems={selectedTopics} kinds={["topic"]} mode="subtopic" /><><span className="mx-1">or</span><DialogConfirm onConfirm={async () => {
          if (!projectId) {
            return;
          }
          const jobId = await deleteCustomAggregateCategory({
            data: {
              project_id: projectId,
              kind: "sub_topic",
              names: selectedTopics
            }
          });
          spawnToast(jobId, "Deleting custom categories ...", () => {
            client.invalidateQueries({
              queryKey: [QUERY_KEY_FETCH_CUSTOM_AGGREGATE_CATEGORIES]
            });
            client.invalidateQueries({
              queryKey: [QUERY_KEY_INTERACTION_AGGREGATES]
            });
            client.invalidateQueries({
              queryKey: [QUERY_KEY_USER_INTENTS_PAGE__INTENTS]
            });
            client.refetchQueries({
              queryKey: [QUERY_KEY_USER_INTENTS_PAGE__ACTIONS]
            });
            client.invalidateQueries({
              queryKey: [QUERY_KEY_USER_QUERIES]
            });
            client.invalidateQueries({
              queryKey: [QUERY_KEY_USER_INTENTS_PAGE__TOTALS]
            });
            client.refetchQueries({
              queryKey: [QUERY_KEY_USER_TOPIC__TOTALS]
            });
          });
          setSelectedTopics([]);
        }}><Button size="sm" className="flex items-center rounded-base border border-accent-foreground text-[12px] text-accent-foreground hover:bg-transparent hover:text-accent-foreground" variant="ghost"><Trash size={12} className="mr-2" /><span>Delete</span></Button></DialogConfirm></></>;
    $[3] = client;
    $[4] = deleteCustomAggregateCategory;
    $[5] = projectId;
    $[6] = selectedTopics;
    $[7] = setSelectedTopics;
    $[8] = spawnToast;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== t3) {
    t4 = <div className="flex items-center gap-2">{t3}</div>;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== setSelectedTopics) {
    t5 = <XCircle size={12} className="ml-2 cursor-pointer" onClick={() => setSelectedTopics([])} />;
    $[12] = setSelectedTopics;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t1 || $[15] !== t4 || $[16] !== t5) {
    t6 = <BottomCtaPortal>{t1}{t2}{t4}{t5}</BottomCtaPortal>;
    $[14] = t1;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  return t6;
};
type InteractionsCtaProps = {
  mode?: 'user_query' | 'interaction';
  selectedQueries: string[];
  projectId: string;
  setSelectedQueries: (s: string[]) => void;
  selectedLabels?: string[][];
  appliedFilters?: Array<{
    column: string;
  }>;
  placement?: 'fixed' | 'inline';
};
function computeCommonLabels(selectedLabels: string[][]): string[] {
  if (selectedLabels.length === 0) return [];
  const [first, ...rest] = selectedLabels;
  return first.filter(label => rest.every(labels => labels.includes(label)));
}
function InteractionBulkAddLabelButton(t0) {
  const $ = _c(15);
  const {
    selectedQueries,
    projectId,
    selectedLabels: t1,
    appliedFilters
  } = t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const selectedLabels = t2;
  const {
    availableLabels,
    isLoading
  } = useAvailableLabelNamesByScope(projectId, "interaction");
  const {
    addLabelToMany,
    removeLabelFromMany,
    isAdding
  } = useBulkInteractionLabels(selectedQueries, appliedFilters);
  let t3;
  if ($[2] !== selectedLabels) {
    t3 = computeCommonLabels(selectedLabels);
    $[2] = selectedLabels;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const commonLabels = t3;
  let t4;
  if ($[4] !== addLabelToMany || $[5] !== removeLabelFromMany) {
    t4 = (name, adding) => {
      if (adding) {
        addLabelToMany(name);
      } else {
        removeLabelFromMany(name);
      }
    };
    $[4] = addLabelToMany;
    $[5] = removeLabelFromMany;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== addLabelToMany) {
    t5 = name_0 => {
      addLabelToMany(name_0);
    };
    $[7] = addLabelToMany;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const t6 = isLoading || isAdding;
  let t7;
  if ($[9] !== availableLabels || $[10] !== commonLabels || $[11] !== t4 || $[12] !== t5 || $[13] !== t6) {
    t7 = <LabelPicker currentLabels={commonLabels} availableLabels={availableLabels} showChips={false} keepOpenOnSelect={true} onToggleLabel={t4} onCreateLabel={t5} isLoading={t6} triggerClassName="flex items-center rounded-base border border-accent-foreground text-[12px] hover:bg-transparent" />;
    $[9] = availableLabels;
    $[10] = commonLabels;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  return t7;
}
type Kind = 'topic' | 'model_suggestion' | 'action' | 'category';
function useMoveToKinds(projectId) {
  const $ = _c(7);
  let t0;
  if ($[0] !== projectId) {
    t0 = {
      projectId
    };
    $[0] = projectId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    hasCategoryEnabled
  } = useHasCategoryEnabled(t0);
  let t1;
  if ($[2] !== hasCategoryEnabled) {
    t1 = hasCategoryEnabled ? ["topic", "action", "category"] : ["topic", "action"];
    $[2] = hasCategoryEnabled;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const kinds = t1;
  let t2;
  if ($[4] !== hasCategoryEnabled || $[5] !== kinds) {
    t2 = {
      kinds,
      hasCategoryEnabled
    };
    $[4] = hasCategoryEnabled;
    $[5] = kinds;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}
export const InteractionsCta = t0 => {
  const $ = _c(19);
  const {
    selectedQueries,
    setSelectedQueries,
    projectId,
    mode: t1,
    selectedLabels,
    appliedFilters,
    placement: t2
  } = t0;
  const mode = t1 === undefined ? "interaction" : t1;
  const placement = t2 === undefined ? "fixed" : t2;
  const {
    isCompanyAdmin
  } = useAuthContext();
  const {
    kinds: moveToKinds
  } = useMoveToKinds(projectId);
  if (!isCompanyAdmin) {
    return;
  }
  let t3;
  if ($[0] !== selectedQueries.length) {
    t3 = <div className="mr-2 rounded-[5px] border border-accent-foreground px-1">+{selectedQueries.length}</div>;
    $[0] = selectedQueries.length;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <span className="mr-2">selected</span>;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== appliedFilters || $[4] !== mode || $[5] !== moveToKinds || $[6] !== projectId || $[7] !== selectedLabels || $[8] !== selectedQueries) {
    t5 = selectedQueries.length > 0 && <><MoveToDialog projectId={projectId} selectedItems={selectedQueries} kinds={moveToKinds} mode={mode} /><span>or</span><CreateAggregateCategoryPortalButton kind="topic" items={selectedQueries} projectId={projectId} groupBy={mode} /><span>or</span><CreateAggregateCategoryPortalButton kind="action" items={selectedQueries} projectId={projectId} groupBy={mode} /><span>or</span><InteractionBulkAddLabelButton selectedQueries={selectedQueries} projectId={projectId} selectedLabels={selectedLabels} appliedFilters={appliedFilters} /></>;
    $[3] = appliedFilters;
    $[4] = mode;
    $[5] = moveToKinds;
    $[6] = projectId;
    $[7] = selectedLabels;
    $[8] = selectedQueries;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t5) {
    t6 = <div className="flex items-center gap-2">{t5}</div>;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== setSelectedQueries) {
    t7 = <XCircle size={12} className="ml-2 cursor-pointer" onClick={() => setSelectedQueries([])} />;
    $[12] = setSelectedQueries;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== placement || $[15] !== t3 || $[16] !== t6 || $[17] !== t7) {
    t8 = <BottomCtaPortal placement={placement}>{t3}{t4}{t6}{t7}</BottomCtaPortal>;
    $[14] = placement;
    $[15] = t3;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  return t8;
};
type ConversationsCtaProps = {
  selectedConversations: string[];
  projectId: string;
  setSelectedConversations: (s: string[]) => void;
  selectedLabels?: string[][];
  appliedFilters?: Array<{
    column: string;
  }>;
  placement?: 'fixed' | 'inline';
};
function ConversationBulkAddLabelButton(t0) {
  const $ = _c(15);
  const {
    selectedConversations,
    projectId,
    selectedLabels: t1,
    appliedFilters
  } = t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const selectedLabels = t2;
  const {
    availableLabels,
    isLoading
  } = useAvailableLabelNamesByScope(projectId, "conversation");
  const {
    addLabelToMany,
    removeLabelFromMany,
    isAdding
  } = useBulkConversationLabels(selectedConversations, appliedFilters);
  let t3;
  if ($[2] !== selectedLabels) {
    t3 = computeCommonLabels(selectedLabels);
    $[2] = selectedLabels;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const commonLabels = t3;
  let t4;
  if ($[4] !== addLabelToMany || $[5] !== removeLabelFromMany) {
    t4 = (name, adding) => {
      if (adding) {
        addLabelToMany(name);
      } else {
        removeLabelFromMany(name);
      }
    };
    $[4] = addLabelToMany;
    $[5] = removeLabelFromMany;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== addLabelToMany) {
    t5 = name_0 => {
      addLabelToMany(name_0);
    };
    $[7] = addLabelToMany;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const t6 = isLoading || isAdding;
  let t7;
  if ($[9] !== availableLabels || $[10] !== commonLabels || $[11] !== t4 || $[12] !== t5 || $[13] !== t6) {
    t7 = <LabelPicker currentLabels={commonLabels} availableLabels={availableLabels} showChips={false} keepOpenOnSelect={true} onToggleLabel={t4} onCreateLabel={t5} isLoading={t6} triggerClassName="flex items-center rounded-base border border-accent-foreground text-[12px] hover:bg-transparent" />;
    $[9] = availableLabels;
    $[10] = commonLabels;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  return t7;
}
export const ConversationsCta = t0 => {
  const $ = _c(18);
  const {
    selectedConversations,
    setSelectedConversations,
    projectId,
    selectedLabels,
    appliedFilters,
    placement: t1
  } = t0;
  const placement = t1 === undefined ? "fixed" : t1;
  const {
    isCompanyAdmin
  } = useAuthContext();
  const {
    kinds: moveToKinds
  } = useMoveToKinds(projectId);
  if (!isCompanyAdmin) {
    return;
  }
  let t2;
  if ($[0] !== selectedConversations.length) {
    t2 = <div className="mr-2 rounded-[5px] border border-accent-foreground px-1">+{selectedConversations.length}</div>;
    $[0] = selectedConversations.length;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span className="mr-2">selected</span>;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== appliedFilters || $[4] !== moveToKinds || $[5] !== projectId || $[6] !== selectedConversations || $[7] !== selectedLabels) {
    t4 = selectedConversations.length > 0 && <><MoveToDialog projectId={projectId} selectedItems={selectedConversations} kinds={moveToKinds} mode="conversation" /><span>or</span><CreateAggregateCategoryPortalButton kind="topic" items={selectedConversations} projectId={projectId} groupBy="conversation" /><span>or</span><CreateAggregateCategoryPortalButton kind="action" items={selectedConversations} projectId={projectId} groupBy="conversation" /><span>or</span><ConversationBulkAddLabelButton selectedConversations={selectedConversations} projectId={projectId} selectedLabels={selectedLabels} appliedFilters={appliedFilters} /></>;
    $[3] = appliedFilters;
    $[4] = moveToKinds;
    $[5] = projectId;
    $[6] = selectedConversations;
    $[7] = selectedLabels;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t4) {
    t5 = <div className="flex items-center gap-2">{t4}</div>;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== setSelectedConversations) {
    t6 = <XCircle size={12} className="ml-2 cursor-pointer" onClick={() => setSelectedConversations([])} />;
    $[11] = setSelectedConversations;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== placement || $[14] !== t2 || $[15] !== t5 || $[16] !== t6) {
    t7 = <BottomCtaPortal placement={placement}>{t2}{t3}{t5}{t6}</BottomCtaPortal>;
    $[13] = placement;
    $[14] = t2;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  return t7;
};
type IntentsCtaProps = {
  selectedIntents: string[];
  projectId: string;
  setSelectedIntents: (s: string[]) => void;
  onlyMergedSelected?: boolean;
  mode: 'user_intent' | 'user_action' | 'topic' | 'keyword';
  showMoveToButton?: boolean;
  showProblems?: boolean;
};
export const IntentsCta = t0 => {
  const $ = _c(63);
  const {
    selectedIntents,
    setSelectedIntents,
    projectId,
    onlyMergedSelected,
    mode,
    children,
    showProblems: t1,
    showMoveToButton: t2
  } = t0;
  const showProblems = t1 === undefined ? false : t1;
  const showMoveToButton = t2 === undefined ? true : t2;
  const {
    spawnToast
  } = useJobToast();
  const client = useQueryClient();
  const {
    isCompanyAdmin
  } = useAuthContext();
  const {
    mutateAsync: deleteCustomAggregateCategory
  } = useDeleteCategory();
  let t3;
  if ($[0] !== client || $[1] !== setSelectedIntents) {
    t3 = {
      mutation: {
        onSuccess: () => {
          client.invalidateQueries({
            queryKey: [QUERY_KEY_INTERACTION_AGGREGATES]
          });
          client.invalidateQueries({
            queryKey: [QUERY_KEY_USER_INTENTS_PAGE__ACTIONS]
          });
          setSelectedIntents([]);
        }
      }
    };
    $[0] = client;
    $[1] = setSelectedIntents;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const {
    mutate: unmergeIntents
  } = useUnmapIntent(t3);
  let t4;
  if ($[3] !== client || $[4] !== setSelectedIntents) {
    t4 = {
      mutation: {
        onSuccess: () => {
          client.invalidateQueries({
            queryKey: [QUERY_KEY_INTERACTION_AGGREGATES]
          });
          client.invalidateQueries({
            queryKey: [QUERY_KEY_USER_INTENTS_PAGE__ACTIONS]
          });
          setSelectedIntents([]);
        }
      }
    };
    $[3] = client;
    $[4] = setSelectedIntents;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const {
    mutate: unmergeTopics
  } = useUnmapTopic(t4);
  let t5;
  if ($[6] !== mode || $[7] !== projectId || $[8] !== unmergeIntents || $[9] !== unmergeTopics) {
    t5 = value => mode === "user_action" ? unmergeIntents({
      data: {
        intent: value,
        project_id: projectId
      }
    }) : unmergeTopics({
      data: {
        topic: value,
        project_id: projectId
      }
    });
    $[6] = mode;
    $[7] = projectId;
    $[8] = unmergeIntents;
    $[9] = unmergeTopics;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const unmerge = t5;
  let t6;
  if ($[11] !== mode || $[12] !== projectId || $[13] !== selectedIntents || $[14] !== setSelectedIntents) {
    t6 = t7 => {
      const {
        children: children_0
      } = t7;
      if (mode === "user_action") {
        return <MergedUserIntentDialog onSuccess={() => setSelectedIntents([])} projectId={projectId} selectedUserIntents={selectedIntents}>{children_0}</MergedUserIntentDialog>;
      } else {
        if (mode === "topic") {
          return <MergedTopicDialog onSuccess={() => setSelectedIntents([])} projectId={projectId} selectedUserIntents={selectedIntents}>{children_0}</MergedTopicDialog>;
        } else {
          if (mode === "keyword") {
            return;
          }
        }
      }
      throw new Error("Invalid mode");
    };
    $[11] = mode;
    $[12] = projectId;
    $[13] = selectedIntents;
    $[14] = setSelectedIntents;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const MergeDialog = t6;
  if (!isCompanyAdmin) {
    return;
  }
  let t7;
  if ($[16] !== selectedIntents.length) {
    t7 = <div className="mr-2 rounded-[5px] border border-accent-foreground px-1">+{selectedIntents.length}</div>;
    $[16] = selectedIntents.length;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <span className="mr-2">selected</span>;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== mode || $[20] !== selectedIntents[0] || $[21] !== selectedIntents.length || $[22] !== setSelectedIntents) {
    t9 = selectedIntents.length > 0 && (mode === "user_action" || mode === "topic" || mode === "user_intent") && <><RenameCategoryDrawer kind={mode} oldName={selectedIntents[0]} onRename={() => setSelectedIntents([])}><Button disabled={selectedIntents.length > 1} size="sm" className="flex items-center rounded-base border border-accent-foreground text-[12px] hover:bg-transparent" variant="ghost"><Pencil size={12} className="mr-2" /><span>Rename</span></Button></RenameCategoryDrawer></>;
    $[19] = mode;
    $[20] = selectedIntents[0];
    $[21] = selectedIntents.length;
    $[22] = setSelectedIntents;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== MergeDialog || $[25] !== mode || $[26] !== selectedIntents.length) {
    t10 = mode !== "user_intent" && <><span className="mr-1">or</span><MergeDialog><Button disabled={selectedIntents.length < 2} size="sm" className="flex items-center rounded-base border border-accent-foreground text-[12px] hover:bg-transparent" variant="ghost"><GitMerge size={12} className="mr-2" /><span>Merge</span></Button></MergeDialog></>;
    $[24] = MergeDialog;
    $[25] = mode;
    $[26] = selectedIntents.length;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== onlyMergedSelected || $[29] !== selectedIntents || $[30] !== unmerge) {
    t11 = onlyMergedSelected && <><span className="mx-1">or</span><DialogConfirm onConfirm={() => {
        selectedIntents.forEach(intent => {
          unmerge(intent);
        });
      }}><Button size="sm" className="flex items-center rounded-base border border-accent-foreground text-[12px] hover:bg-transparent" variant="ghost"><GitMerge size={12} className="mr-2" /><span>Unmerge</span></Button></DialogConfirm></>;
    $[28] = onlyMergedSelected;
    $[29] = selectedIntents;
    $[30] = unmerge;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== mode || $[33] !== projectId || $[34] !== selectedIntents || $[35] !== showMoveToButton || $[36] !== showProblems) {
    t12 = showMoveToButton && <>{mode !== "keyword" && <span className="mx-1">or</span>}<MoveToDialog selectedItems={selectedIntents} projectId={projectId} kinds={showProblems ? ["topic", "model_suggestion"] : ["topic"]} mode={mode === "keyword" ? "keyword" : "action"} /></>;
    $[32] = mode;
    $[33] = projectId;
    $[34] = selectedIntents;
    $[35] = showMoveToButton;
    $[36] = showProblems;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  let t13;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <span className="mx-1">or</span>;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] !== client || $[40] !== deleteCustomAggregateCategory || $[41] !== mode || $[42] !== projectId || $[43] !== selectedIntents || $[44] !== setSelectedIntents || $[45] !== spawnToast) {
    t14 = async () => {
      if (!projectId) {
        return;
      }
      const getKindFromMode = _temp6;
      const kind = getKindFromMode(mode);
      if (!kind) {
        return;
      }
      const jobId = await deleteCustomAggregateCategory({
        data: {
          project_id: projectId,
          kind,
          names: selectedIntents
        }
      });
      spawnToast(jobId, "Deleting custom categories ...", () => {
        client.invalidateQueries({
          queryKey: [QUERY_KEY_FETCH_CUSTOM_AGGREGATE_CATEGORIES]
        });
        client.invalidateQueries({
          queryKey: [QUERY_KEY_INTERACTION_AGGREGATES]
        });
        client.invalidateQueries({
          queryKey: [QUERY_KEY_USER_INTENTS_PAGE__INTENTS]
        });
        client.refetchQueries({
          queryKey: [QUERY_KEY_USER_INTENTS_PAGE__ACTIONS]
        });
        client.invalidateQueries({
          queryKey: [QUERY_KEY_USER_QUERIES]
        });
        client.invalidateQueries({
          queryKey: [QUERY_KEY_USER_INTENTS_PAGE__TOTALS]
        });
        client.refetchQueries({
          queryKey: [QUERY_KEY_USER_TOPIC__TOTALS]
        });
      });
      setSelectedIntents([]);
    };
    $[39] = client;
    $[40] = deleteCustomAggregateCategory;
    $[41] = mode;
    $[42] = projectId;
    $[43] = selectedIntents;
    $[44] = setSelectedIntents;
    $[45] = spawnToast;
    $[46] = t14;
  } else {
    t14 = $[46];
  }
  let t15;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <Button size="sm" className="flex items-center rounded-base border border-accent-foreground text-[12px] text-accent-foreground" variant="outline"><Trash size={12} className="mr-2" /><span>Delete</span></Button>;
    $[47] = t15;
  } else {
    t15 = $[47];
  }
  let t16;
  if ($[48] !== t14) {
    t16 = <>{t13}<DialogConfirm onConfirm={t14}>{t15}</DialogConfirm></>;
    $[48] = t14;
    $[49] = t16;
  } else {
    t16 = $[49];
  }
  let t17;
  if ($[50] !== children || $[51] !== t10 || $[52] !== t11 || $[53] !== t12 || $[54] !== t16 || $[55] !== t9) {
    t17 = <div className="flex items-center gap-2">{t9}{t10}{t11}{t12}{children}{t16}</div>;
    $[50] = children;
    $[51] = t10;
    $[52] = t11;
    $[53] = t12;
    $[54] = t16;
    $[55] = t9;
    $[56] = t17;
  } else {
    t17 = $[56];
  }
  let t18;
  if ($[57] !== setSelectedIntents) {
    t18 = <XCircle size={12} className="ml-2 cursor-pointer" onClick={() => setSelectedIntents([])} />;
    $[57] = setSelectedIntents;
    $[58] = t18;
  } else {
    t18 = $[58];
  }
  let t19;
  if ($[59] !== t17 || $[60] !== t18 || $[61] !== t7) {
    t19 = <BottomCtaPortal>{t7}{t8}{t17}{t18}</BottomCtaPortal>;
    $[59] = t17;
    $[60] = t18;
    $[61] = t7;
    $[62] = t19;
  } else {
    t19 = $[62];
  }
  return t19;
};
export function MoveToDialog(t0) {
  const $ = _c(163);
  const {
    selectedItems,
    projectId,
    kinds: t1,
    mode,
    parentName: t2,
    onSuccess,
    timeRange: timeRangeOverride
  } = t0;
  let t3;
  if ($[0] !== t1) {
    t3 = t1 === undefined ? ["topic", "model_suggestion"] : t1;
    $[0] = t1;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const kinds = t3;
  const parentName = t2 === undefined ? null : t2;
  const {
    timeRange: globalTimeRange,
    aggregateBy,
    filters
  } = useGlobalFiltersContext();
  const timeRange = timeRangeOverride ?? globalTimeRange;
  const {
    isMappedProject,
    failureCategories
  } = useProjectsContext();
  const queryClient = useQueryClient();
  const {
    spawnToast
  } = useJobToast();
  const {
    data: projectConfig
  } = useConfig(projectId);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [pagOffset, setPagOffset] = useState(0);
  const [stepIndex, setStepIndex] = useState(0);
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [];
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const [selectedPathValues, setSelectedPathValues] = useState(t4);
  const debouncedSearch = useDebounceValue(search);
  const pagSize = 10;
  const [selectedKind, setSelectedKind] = useState(kinds?.[0]);
  let t5;
  if ($[3] !== selectedItems) {
    t5 = selectedItems.includes("other");
    $[3] = selectedItems;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const containsOther = t5;
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      type: "objectORnumberORboolean"
    };
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const [problemsProblemFilters] = useQueryParamStateV2(PARAMS_KEY_LLM_PROBLEMS_PROBLEM_FILTERS, undefined, t6);
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      type: "string"
    };
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  const [userIntentVerticalTab] = useQueryParamStateV2(PARAMS_KEY_USER_INTENTS_VERTICAL_TAB_STRING, undefined, t7);
  let t8;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      type: "string"
    };
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  const [intentsTableGroupBy] = useQueryParamStateV2(PARAMS_KEY_INTENTS_TABLE_GROUPBY, undefined, t8);
  let t9;
  bb0: {
    if (problemsProblemFilters && problemsProblemFilters.kind === "type_of_problem_v2") {
      t9 = problemsProblemFilters?.values?.[0];
      break bb0;
    }
    t9 = userIntentVerticalTab;
  }
  const selectedProblem = t9;
  let t10;
  bb1: {
    if (isMappedProject) {
      t10 = "Moving actions in custom projects is currently not supported.";
      break bb1;
    }
    if (containsOther) {
      t10 = "Moving the \"Other\" action is currently not supported.";
      break bb1;
    }
    if (selectedProblem === "thumbs down") {
      t10 = "Moving items from the \"Thumbs down\" tab is currently not supported.";
      break bb1;
    }
    t10 = undefined;
  }
  const moveButtonDisabledReason = t10;
  const projectAggregates = projectConfig?.aggregates;
  let t11;
  bb2: {
    if (mode !== "action") {
      t11 = undefined;
      break bb2;
    }
    let t12;
    if ($[8] !== projectAggregates || $[9] !== selectedKind) {
      t12 = resolveAggregateMovePath(projectAggregates, selectedKind);
      $[8] = projectAggregates;
      $[9] = selectedKind;
      $[10] = t12;
    } else {
      t12 = $[10];
    }
    t11 = t12;
  }
  const actionMovePath = t11;
  const destinationSteps = actionMovePath?.destinationSteps;
  const usesProgressiveSteps = (destinationSteps?.length ?? 0) > 1;
  const isFinalDestinationStep = !usesProgressiveSteps || stepIndex >= (destinationSteps?.length ?? 1) - 1;
  const currentStepNode = destinationSteps?.[stepIndex];
  let t12;
  if ($[11] !== currentStepNode || $[12] !== selectedKind) {
    t12 = currentStepNode ? mapAggregateNodeToQueryAxis(currentStepNode) : fallbackQueryAxisForKind(selectedKind);
    $[11] = currentStepNode;
    $[12] = selectedKind;
    $[13] = t12;
  } else {
    t12 = $[13];
  }
  const currentAxis = t12;
  let t13;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => {
      setStepIndex(0);
      setSelectedPathValues([]);
      setPagOffset(0);
      setSearch("");
    };
    $[14] = t13;
  } else {
    t13 = $[14];
  }
  const resetDestinationSteps = t13;
  let t14;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => {
      resetDestinationSteps();
    };
    $[15] = t14;
  } else {
    t14 = $[15];
  }
  let t15;
  if ($[16] !== open || $[17] !== selectedKind) {
    t15 = [selectedKind, open, resetDestinationSteps];
    $[16] = open;
    $[17] = selectedKind;
    $[18] = t15;
  } else {
    t15 = $[18];
  }
  useEffect(t14, t15);
  let t16;
  bb3: {
    if (!destinationSteps?.length || !selectedPathValues.length) {
      let t17;
      if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
        t17 = [];
        $[19] = t17;
      } else {
        t17 = $[19];
      }
      t16 = t17;
      break bb3;
    }
    let t17;
    if ($[20] !== destinationSteps || $[21] !== selectedPathValues) {
      let t18;
      if ($[23] !== destinationSteps) {
        t18 = (value, index) => {
          const step = destinationSteps[index];
          if (!step) {
            return [];
          }
          const axis = mapAggregateNodeToQueryAxis(step);
          return [{
            column: axis.filterColumn,
            values: [value],
            is_excluded: false
          } satisfies GenericFilter];
        };
        $[23] = destinationSteps;
        $[24] = t18;
      } else {
        t18 = $[24];
      }
      t17 = selectedPathValues.flatMap(t18);
      $[20] = destinationSteps;
      $[21] = selectedPathValues;
      $[22] = t17;
    } else {
      t17 = $[22];
    }
    t16 = t17;
  }
  const ancestorFilters = t16;
  const searchColumn = currentAxis.groupBy.kind === "custom_aggregate" || currentAxis.groupBy.kind === "conversation_custom_aggregate" ? currentAxis.filterColumn : currentAxis.groupBy.kind as string | undefined ?? currentAxis.filterColumn;
  let t17;
  if ($[25] !== debouncedSearch || $[26] !== searchColumn) {
    t17 = debouncedSearch ? [{
      column: searchColumn,
      values: [],
      search: debouncedSearch,
      is_excluded: false
    } as GenericFilter] : [];
    $[25] = debouncedSearch;
    $[26] = searchColumn;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  let t18;
  if ($[28] !== ancestorFilters || $[29] !== filters || $[30] !== t17) {
    t18 = [...filters, ...ancestorFilters, ...t17];
    $[28] = ancestorFilters;
    $[29] = filters;
    $[30] = t17;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  let t19;
  if ($[32] !== currentAxis.groupBy || $[33] !== projectId || $[34] !== t18 || $[35] !== timeRange) {
    t19 = {
      filters: t18,
      project_id: projectId,
      time_range: timeRange,
      limit: 100000,
      group_by: currentAxis.groupBy
    };
    $[32] = currentAxis.groupBy;
    $[33] = projectId;
    $[34] = t18;
    $[35] = timeRange;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  const aggregatePayload = t19;
  const t20 = !!projectId;
  let t21;
  if ($[37] !== aggregateBy || $[38] !== aggregatePayload || $[39] !== t20) {
    t21 = {
      payload: aggregatePayload,
      enabled: t20,
      aggregateBy
    };
    $[37] = aggregateBy;
    $[38] = aggregatePayload;
    $[39] = t20;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  const {
    data: aggregatesData,
    isLoading: isLoadingAggregates
  } = useFetchInteractionAggregates(t21);
  const t22 = usesProgressiveSteps ? undefined : currentAxis.moveKind;
  let t23;
  if ($[41] !== projectId || $[42] !== t22) {
    t23 = {
      payload: {
        project_id: projectId,
        aggregate_kind: t22
      }
    };
    $[41] = projectId;
    $[42] = t22;
    $[43] = t23;
  } else {
    t23 = $[43];
  }
  const {
    data: customAggregateCategoriesData,
    isLoading: isLoadingCustomAggregateCategories
  } = useFetchCustomAggregateCategories(t23);
  let t24;
  bb4: {
    let t25;
    if ($[44] !== customAggregateCategoriesData) {
      t25 = customAggregateCategoriesData ?? [];
      $[44] = customAggregateCategoriesData;
      $[45] = t25;
    } else {
      t25 = $[45];
    }
    const categories = t25;
    if (!usesProgressiveSteps || !currentStepNode) {
      t24 = categories;
      break bb4;
    }
    let t26;
    if ($[46] !== destinationSteps) {
      t26 = destinationSteps ?? [];
      $[46] = destinationSteps;
      $[47] = t26;
    } else {
      t26 = $[47];
    }
    let t27;
    if ($[48] !== categories || $[49] !== currentStepNode || $[50] !== selectedPathValues || $[51] !== t26) {
      t27 = filterCustomCategoriesByAncestorPath(categories, currentStepNode, selectedPathValues, t26);
      $[48] = categories;
      $[49] = currentStepNode;
      $[50] = selectedPathValues;
      $[51] = t26;
      $[52] = t27;
    } else {
      t27 = $[52];
    }
    t24 = t27;
  }
  const scopedCustomCategories = t24;
  const isLoading = isLoadingAggregates || isLoadingCustomAggregateCategories;
  let t25;
  bb5: {
    if (isLoading) {
      let t26;
      if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
        t26 = [];
        $[53] = t26;
      } else {
        t26 = $[53];
      }
      t25 = t26;
      break bb5;
    }
    let t26;
    if ($[54] !== aggregatesData?.aggregates || $[55] !== failureCategories || $[56] !== scopedCustomCategories || $[57] !== selectedKind || $[58] !== stepIndex || $[59] !== usesProgressiveSteps) {
      let a;
      let b;
      if (selectedKind === "model_suggestion" && stepIndex === 0 && !usesProgressiveSteps) {
        a = aggregatesData?.aggregates.map(_temp7).filter(_temp8) ?? [];
        b = failureCategories.map(_temp9).filter(_temp10) ?? [];
      } else {
        a = aggregatesData?.aggregates.map(_temp11) ?? [];
        b = scopedCustomCategories.map(_temp12) ?? [];
      }
      const mergedMap = new Map();
      [...a, ...b].forEach(name => {
        const lower = name.toLowerCase();
        if (!mergedMap.has(lower)) {
          mergedMap.set(lower, name);
        }
      });
      t26 = Array.from(mergedMap.values()).sort(_temp13);
      $[54] = aggregatesData?.aggregates;
      $[55] = failureCategories;
      $[56] = scopedCustomCategories;
      $[57] = selectedKind;
      $[58] = stepIndex;
      $[59] = usesProgressiveSteps;
      $[60] = t26;
    } else {
      t26 = $[60];
    }
    t25 = t26;
  }
  const combined = t25;
  const start = pagOffset * 10;
  let t26;
  if ($[61] !== combined || $[62] !== start) {
    t26 = combined.slice(start, start + 10);
    $[61] = combined;
    $[62] = start;
    $[63] = t26;
  } else {
    t26 = $[63];
  }
  const aggregates = t26;
  const isEmpty = aggregates.length === 0;
  let t27;
  if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = {
      type: "filter",
      defaultValue: []
    };
    $[64] = t27;
  } else {
    t27 = $[64];
  }
  const {
    filters: intentFilters
  } = useQueryFilters(PARAMS_KEY_USER_INTENTS_FILTERS, t27);
  let t28;
  if ($[65] !== intentFilters) {
    t28 = intentFilters.find(_temp14)?.values?.[0];
    $[65] = intentFilters;
    $[66] = t28;
  } else {
    t28 = $[66];
  }
  const selectedTopic = t28;
  const verticalTabSubtopicSource = mode === "action" && intentsTableGroupBy === "category" ? userIntentVerticalTab : null;
  let t29;
  if ($[67] !== intentFilters || $[68] !== parentName || $[69] !== selectedProblem || $[70] !== verticalTabSubtopicSource) {
    t29 = {
      intentFilters,
      parentName,
      selectedProblem,
      verticalTabValue: verticalTabSubtopicSource
    };
    $[67] = intentFilters;
    $[68] = parentName;
    $[69] = selectedProblem;
    $[70] = verticalTabSubtopicSource;
    $[71] = t29;
  } else {
    t29 = $[71];
  }
  const moveFromParentContext = t29;
  let t30;
  if ($[72] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = {};
    $[72] = t30;
  } else {
    t30 = $[72];
  }
  const {
    mutateAsync: moveAggregates
  } = useMoveCategories(t30);
  let t31;
  if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = {};
    $[73] = t31;
  } else {
    t31 = $[73];
  }
  const {
    mutateAsync: moveInteractions
  } = useMoveInteractions(t31);
  let t32;
  if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = {};
    $[74] = t32;
  } else {
    t32 = $[74];
  }
  const {
    mutateAsync: invalidateCache
  } = useInvalidateCache(t32);
  let t33;
  bb6: {
    if (!usesProgressiveSteps || !currentStepNode) {
      t33 = "Available targets";
      break bb6;
    }
    let t34;
    if ($[75] !== currentStepNode) {
      t34 = getDestinationStepLabel(currentStepNode);
      $[75] = currentStepNode;
      $[76] = t34;
    } else {
      t34 = $[76];
    }
    t33 = `Select ${t34}`;
  }
  const targetsSectionTitle = t33;
  let t34;
  if ($[77] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = value_0 => {
      setSelectedPathValues(prev => [...prev, value_0]);
      setStepIndex(_temp15);
      setPagOffset(0);
      setSearch("");
    };
    $[77] = t34;
  } else {
    t34 = $[77];
  }
  const advanceDestinationStep = t34;
  let t35;
  if ($[78] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = () => {
      setSelectedPathValues(_temp16);
      setStepIndex(_temp17);
      setPagOffset(0);
      setSearch("");
    };
    $[78] = t35;
  } else {
    t35 = $[78];
  }
  const goBackDestinationStep = t35;
  let t36;
  if ($[79] !== invalidateCache || $[80] !== mode || $[81] !== moveAggregates || $[82] !== moveFromParentContext || $[83] !== moveInteractions || $[84] !== onSuccess || $[85] !== parentName || $[86] !== projectAggregates || $[87] !== projectId || $[88] !== queryClient || $[89] !== selectedItems || $[90] !== selectedKind || $[91] !== selectedTopic || $[92] !== spawnToast) {
    t36 = async destinationName => {
      if (!selectedItems?.length) {
        toast.error("No items selected to move");
        return;
      }
      const invalidateAll = async () => {
        await invalidateCache({
          projectId
        });
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY_INTERACTION_AGGREGATES]
        });
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY_USER_INTENTS_PAGE__INTENTS]
        });
        queryClient.refetchQueries({
          queryKey: [QUERY_KEY_USER_INTENTS_PAGE__ACTIONS]
        });
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY_USER_QUERIES]
        });
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY_USER_INTENTS_PAGE__TOTALS]
        });
        queryClient.refetchQueries({
          queryKey: [QUERY_KEY_USER_TOPIC__TOTALS]
        });
      };
      const moveSubtopicAggregates = opts => moveAggregates({
        data: {
          project_id: projectId,
          category_kind: "sub_topic",
          category_names: opts.names,
          from_parent_kind: selectedKind,
          from_parent_name: opts.fromName,
          to_parent_kind: selectedKind,
          to_parent_name: opts.toName
        }
      });
      const moveActionAggregates = opts_0 => moveAggregates({
        data: {
          project_id: projectId,
          category_kind: opts_0.categoryKind,
          category_names: opts_0.names,
          from_parent_kind: opts_0.fromParentKind,
          from_parent_name: opts_0.fromName,
          to_parent_kind: opts_0.toParentKind,
          to_parent_name: opts_0.toName
        }
      });
      const moveFilteredInteractions = type => moveInteractions({
        data: {
          project_id: projectId,
          kind: selectedKind,
          name: destinationName,
          filters: {
            type,
            values: selectedItems
          }
        }
      });
      const jobId = await (async () => {
        if (mode === "keyword") {
          return moveFilteredInteractions("keyword");
        }
        if (mode === "user_query") {
          return moveFilteredInteractions("query");
        }
        if (mode === "interaction") {
          return moveFilteredInteractions("id");
        }
        if (mode === "conversation") {
          return moveFilteredInteractions("conversation_id");
        }
        if (mode === "subtopic") {
          return moveSubtopicAggregates({
            fromName: selectedTopic ?? parentName,
            toName: destinationName,
            names: selectedItems
          });
        }
        if (mode === "action") {
          const parentKinds = resolveActionMoveParentKinds(projectAggregates, selectedKind);
          const fromName = resolveMoveFromParentName(parentKinds.fromParentKind, moveFromParentContext);
          return moveActionAggregates({
            fromName,
            toName: destinationName,
            names: selectedItems,
            fromParentKind: parentKinds.fromParentKind,
            toParentKind: parentKinds.toParentKind,
            categoryKind: parentKinds.categoryKind
          });
        }
      })();
      if (!jobId) {
        toast.error("Failed to move items");
        return;
      }
      spawnToast(jobId, `Moving items into ${destinationName}`, async () => {
        await invalidateAll();
        onSuccess?.();
      });
      setOpen(false);
    };
    $[79] = invalidateCache;
    $[80] = mode;
    $[81] = moveAggregates;
    $[82] = moveFromParentContext;
    $[83] = moveInteractions;
    $[84] = onSuccess;
    $[85] = parentName;
    $[86] = projectAggregates;
    $[87] = projectId;
    $[88] = queryClient;
    $[89] = selectedItems;
    $[90] = selectedKind;
    $[91] = selectedTopic;
    $[92] = spawnToast;
    $[93] = t36;
  } else {
    t36 = $[93];
  }
  const performMove = t36;
  let t37;
  if ($[94] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = () => {
      setOpen(true);
    };
    $[94] = t37;
  } else {
    t37 = $[94];
  }
  const t38 = !selectedItems.length || isMappedProject || containsOther || selectedProblem === "thumbs down";
  let t39;
  let t40;
  if ($[95] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = <ArrowRightLeft size={12} className="mr-2" />;
    t40 = <span>Move to</span>;
    $[95] = t39;
    $[96] = t40;
  } else {
    t39 = $[95];
    t40 = $[96];
  }
  let t41;
  if ($[97] !== moveButtonDisabledReason || $[98] !== t38) {
    t41 = <DialogTrigger asChild={true}><Button onClick={t37} disabledMessage={moveButtonDisabledReason} disabled={t38} size="sm" className="flex items-center rounded-base border border-accent-foreground text-[12px] hover:bg-transparent" variant="ghost">{t39}{t40}</Button></DialogTrigger>;
    $[97] = moveButtonDisabledReason;
    $[98] = t38;
    $[99] = t41;
  } else {
    t41 = $[99];
  }
  let t42;
  if ($[100] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = <NavElement Icon={FlaskConical} text="Move to" coloredIcon={true} isActive={true} showTooltip={false} />;
    $[100] = t42;
  } else {
    t42 = $[100];
  }
  let t43;
  if ($[101] === Symbol.for("react.memo_cache_sentinel")) {
    t43 = <DialogTitle><div className="flex items-center justify-between px-7">{t42}<DialogClose><X size={20} className="text-muted-foreground hover:cursor-pointer" /></DialogClose></div></DialogTitle>;
    $[101] = t43;
  } else {
    t43 = $[101];
  }
  let t44;
  if ($[102] !== selectedItems.length) {
    t44 = <p>Selected items{" "}<span className="text-xs text-muted-foreground underline decoration-neutral-600 underline-offset-2">(+{selectedItems.length})</span></p>;
    $[102] = selectedItems.length;
    $[103] = t44;
  } else {
    t44 = $[103];
  }
  let t45;
  if ($[104] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = <ListChecks className="h-4 w-4" />;
    $[104] = t45;
  } else {
    t45 = $[104];
  }
  let t46;
  if ($[105] !== selectedItems) {
    t46 = selectedItems.map(_temp18);
    $[105] = selectedItems;
    $[106] = t46;
  } else {
    t46 = $[106];
  }
  let t47;
  if ($[107] !== t46) {
    t47 = <ul className="max-h-25 list-disc overflow-y-auto pl-5">{t46}</ul>;
    $[107] = t46;
    $[108] = t47;
  } else {
    t47 = $[108];
  }
  let t48;
  if ($[109] !== t44 || $[110] !== t47) {
    t48 = <CreateCategoryDialogSection title={t44} icon={t45}>{t47}</CreateCategoryDialogSection>;
    $[109] = t44;
    $[110] = t47;
    $[111] = t48;
  } else {
    t48 = $[111];
  }
  let t49;
  if ($[112] === Symbol.for("react.memo_cache_sentinel")) {
    t49 = <Pencil className="h-4 w-4" />;
    $[112] = t49;
  } else {
    t49 = $[112];
  }
  let t50;
  if ($[113] === Symbol.for("react.memo_cache_sentinel")) {
    t50 = <p className="text-muted-foreground">Aggregate kind:</p>;
    $[113] = t50;
  } else {
    t50 = $[113];
  }
  let t51;
  if ($[114] === Symbol.for("react.memo_cache_sentinel")) {
    t51 = v => {
      setSelectedKind(v);
      resetDestinationSteps();
    };
    $[114] = t51;
  } else {
    t51 = $[114];
  }
  let t52;
  if ($[115] !== kinds || $[116] !== selectedKind) {
    t52 = <div className="flex items-center gap-2">{t50}<CategoryKindsSelector availableKinds={kinds} value={selectedKind} onChange={t51} /></div>;
    $[115] = kinds;
    $[116] = selectedKind;
    $[117] = t52;
  } else {
    t52 = $[117];
  }
  let t53;
  if ($[118] === Symbol.for("react.memo_cache_sentinel")) {
    t53 = s => {
      setSearch(s);
      setPagOffset(0);
    };
    $[118] = t53;
  } else {
    t53 = $[118];
  }
  let t54;
  if ($[119] !== search) {
    t54 = <SearchInputV2 placeholder="Search here..." search={search} setSearch={t53} className="w-full" />;
    $[119] = search;
    $[120] = t54;
  } else {
    t54 = $[120];
  }
  let t55;
  if ($[121] !== t52 || $[122] !== t54) {
    t55 = <div className="mb-6 flex items-center justify-between gap-2 pb-2">{t52}{t54}</div>;
    $[121] = t52;
    $[122] = t54;
    $[123] = t55;
  } else {
    t55 = $[123];
  }
  let t56;
  if ($[124] !== destinationSteps || $[125] !== selectedPathValues || $[126] !== usesProgressiveSteps) {
    t56 = usesProgressiveSteps && selectedPathValues.length > 0 && <div className="mb-4 flex items-center gap-2 text-sm text-muted-foreground"><Button type="button" size="sm" variant="ghost" className="h-7 px-2" onClick={goBackDestinationStep}><ArrowLeft size={14} className="mr-1" />Back</Button><p className="truncate">{selectedPathValues.map((value_1, index_0) => {
          const step_0 = destinationSteps?.[index_0];
          const label = step_0 ? getDestinationStepLabel(step_0) : "Step";
          return `${label}: ${capitalize(value_1)}`;
        }).join(" \u2192 ")}</p></div>;
    $[124] = destinationSteps;
    $[125] = selectedPathValues;
    $[126] = usesProgressiveSteps;
    $[127] = t56;
  } else {
    t56 = $[127];
  }
  let t57;
  if ($[128] !== aggregates || $[129] !== isFinalDestinationStep || $[130] !== performMove) {
    let t58;
    if ($[132] !== aggregates.length || $[133] !== isFinalDestinationStep || $[134] !== performMove) {
      t58 = agg_1 => <li className={cn("flex items-center gap-2 border-b odd:bg-[rgba(255,255,255,0.02)] hover:cursor-pointer hover:bg-accent hover:text-accent-foreground", {
        "last:border-b-0": aggregates.length > 1
      })} key={agg_1}>{isFinalDestinationStep ? <DialogConfirmLoading description={`This will move the selected entities to "${capitalize(agg_1)}"`} onConfirm={async () => {
          await performMove(agg_1);
        }}><p className="w-full px-2 py-2">{capitalize(agg_1)}</p></DialogConfirmLoading> : <button type="button" className="w-full px-2 py-2 text-left" onClick={() => advanceDestinationStep(agg_1)}>{capitalize(agg_1)}</button>}</li>;
      $[132] = aggregates.length;
      $[133] = isFinalDestinationStep;
      $[134] = performMove;
      $[135] = t58;
    } else {
      t58 = $[135];
    }
    t57 = aggregates.map(t58);
    $[128] = aggregates;
    $[129] = isFinalDestinationStep;
    $[130] = performMove;
    $[131] = t57;
  } else {
    t57 = $[131];
  }
  let t58;
  if ($[136] !== t57) {
    t58 = <ul className="flex max-h-100 flex-col">{t57}</ul>;
    $[136] = t57;
    $[137] = t58;
  } else {
    t58 = $[137];
  }
  let t59;
  if ($[138] !== isEmpty || $[139] !== t58) {
    t59 = <div className="grow overflow-hidden rounded-lg border border-neutral-900 bg-[rgba(255,255,255,0.01)]"><WithEmptyState isEmpty={isEmpty} title="No custom categories found" description=" ">{t58}</WithEmptyState></div>;
    $[138] = isEmpty;
    $[139] = t58;
    $[140] = t59;
  } else {
    t59 = $[140];
  }
  let t60;
  if ($[141] !== combined.length || $[142] !== isEmpty || $[143] !== pagOffset) {
    t60 = !isEmpty && <div className="flex w-full items-center justify-end pb-2"><Pagination pageSize={10} setPageIndex={setPagOffset} pageIndex={pagOffset} totalPages={Math.ceil(combined.length / 10)} possibleSizes={[10]} /></div>;
    $[141] = combined.length;
    $[142] = isEmpty;
    $[143] = pagOffset;
    $[144] = t60;
  } else {
    t60 = $[144];
  }
  let t61;
  if ($[145] !== isLoading || $[146] !== t59 || $[147] !== t60) {
    t61 = <section><WithLoadingContainer hideChildren={true} loading={isLoading} className="flex h-full min-h-50 flex-col justify-center gap-4">{t59}{t60}</WithLoadingContainer></section>;
    $[145] = isLoading;
    $[146] = t59;
    $[147] = t60;
    $[148] = t61;
  } else {
    t61 = $[148];
  }
  let t62;
  if ($[149] !== t55 || $[150] !== t56 || $[151] !== t61) {
    t62 = <div>{t55}{t56}{t61}</div>;
    $[149] = t55;
    $[150] = t56;
    $[151] = t61;
    $[152] = t62;
  } else {
    t62 = $[152];
  }
  let t63;
  if ($[153] !== t62 || $[154] !== targetsSectionTitle) {
    t63 = <CreateCategoryDialogSection title={targetsSectionTitle} icon={t49}>{t62}</CreateCategoryDialogSection>;
    $[153] = t62;
    $[154] = targetsSectionTitle;
    $[155] = t63;
  } else {
    t63 = $[155];
  }
  let t64;
  if ($[156] !== t48 || $[157] !== t63) {
    t64 = <DialogContentWrapper className="gap-0 p-0">{t43}{t48}{t63}</DialogContentWrapper>;
    $[156] = t48;
    $[157] = t63;
    $[158] = t64;
  } else {
    t64 = $[158];
  }
  let t65;
  if ($[159] !== open || $[160] !== t41 || $[161] !== t64) {
    t65 = <><Dialog open={open} onOpenChange={setOpen} modal={true}>{t41}{t64}</Dialog></>;
    $[159] = open;
    $[160] = t41;
    $[161] = t64;
    $[162] = t65;
  } else {
    t65 = $[162];
  }
  return t65;
}
function _temp18(item) {
  return <li key={item} className="text-sm text-muted-foreground">{capitalize(item)}</li>;
}
function _temp17(prev_2) {
  return Math.max(0, prev_2 - 1);
}
function _temp16(prev_1) {
  return prev_1.slice(0, -1);
}
function _temp15(prev_0) {
  return prev_0 + 1;
}
function _temp14(elem) {
  return elem.column === "topic";
}
function _temp13(x, y) {
  return x.localeCompare(y);
}
function _temp12(e) {
  return e.value;
}
function _temp11(agg_0) {
  return agg_0.groupName;
}
function _temp10(val_0) {
  return val_0 != "thumbs down";
}
function _temp9(cat) {
  return cat.value;
}
function _temp8(val) {
  return val != "thumbs down";
}
function _temp7(agg) {
  return agg.groupName;
}
export const TopicChip = props => {
  const $ = _c(27);
  const isMostRelevant = props.mode === "most_relevant_topic";
  if (!props.mode) {
    let t0;
    if ($[0] !== props.children) {
      t0 = <p className="text-[16px] text-muted-foreground">{props.children}</p>;
      $[0] = props.children;
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    return t0;
  }
  const isInteractive = !!props.onClick;
  const t0 = isInteractive ? "button" : undefined;
  const t1 = isInteractive ? 0 : undefined;
  const t2 = props.onClick;
  let t3;
  if ($[2] !== isInteractive || $[3] !== props) {
    t3 = isInteractive ? e => onEnterPress(e, () => props.onClick?.()) : undefined;
    $[2] = isInteractive;
    $[3] = props;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = !isInteractive;
  let t5;
  if ($[5] !== isInteractive || $[6] !== isMostRelevant || $[7] !== props.className || $[8] !== props.i || $[9] !== t4) {
    t5 = cn("flex items-center gap-1 pr-2 font-medium text-accent-foreground", {
      "hover:cursor-pointer": isInteractive,
      "cursor-default": t4
    }, isMostRelevant && borderStyles[props.i % borderStyles.length], props.className);
    $[5] = isInteractive;
    $[6] = isMostRelevant;
    $[7] = props.className;
    $[8] = props.i;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const t6 = props.color ? props.color : colorsWithAlpha[props.i % colorsWithAlpha.length];
  let t7;
  if ($[11] !== t6) {
    t7 = {
      borderColor: t6
    };
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== isMostRelevant || $[14] !== props.color || $[15] !== props.hideIcon || $[16] !== props.i) {
    t8 = isMostRelevant && <><div style={{
        background: props.color ? props.color : colorsWithAlpha[props.i % colorsWithAlpha.length]
      }} className={cn("inline h-1.75 w-1.75 min-w-1.75 rounded-full", bgColors[props.i % borderStyles.length])} />{!props.hideIcon && <FolderOpen className="min-w-max" size={12} />}</>;
    $[13] = isMostRelevant;
    $[14] = props.color;
    $[15] = props.hideIcon;
    $[16] = props.i;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== props.children || $[19] !== props.onClick || $[20] !== t0 || $[21] !== t1 || $[22] !== t3 || $[23] !== t5 || $[24] !== t7 || $[25] !== t8) {
    t9 = <div role={t0} tabIndex={t1} onClick={t2} onKeyDown={t3} className={t5} style={t7}>{t8}{props.children}</div>;
    $[18] = props.children;
    $[19] = props.onClick;
    $[20] = t0;
    $[21] = t1;
    $[22] = t3;
    $[23] = t5;
    $[24] = t7;
    $[25] = t8;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  return t9;
};
export const DEFAULT_SENTIMENT_SCORE = 3;
export const SentimentChip = t0 => {
  const $ = _c(8);
  const {
    sentimentScore
  } = t0;
  if (typeof sentimentScore === "string") {
    let t1;
    if ($[0] !== sentimentScore) {
      t1 = capitalize(sentimentScore.replace("_", " "));
      $[0] = sentimentScore;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    const sentimentLabel = t1;
    let t2;
    if ($[2] !== sentimentLabel) {
      t2 = <SentimentChipByLabel sentimentLabel={sentimentLabel} />;
      $[2] = sentimentLabel;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  let t1;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      1: "Very negative",
      2: "Negative",
      3: "Neutral",
      4: "Positive",
      5: "Very positive"
    };
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const sentimentMap = t1;
  if (typeof sentimentScore === "number" && !Number.isNaN(sentimentScore)) {
    const sentimentLabelIndex = Math.floor(sentimentScore);
    const sentimentLabel_0 = sentimentMap[sentimentLabelIndex];
    let t2;
    if ($[5] !== sentimentLabel_0) {
      t2 = <SentimentChipByLabel sentimentLabel={sentimentLabel_0} />;
      $[5] = sentimentLabel_0;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    return t2;
  }
  let t2;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <SentimentChipByLabel sentimentLabel={sentimentMap[DEFAULT_SENTIMENT_SCORE]} />;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
const sentimentIconComponents: Record<SentimentType, LucideIcon> = {
  Neutral: Meh,
  Positive: Smile,
  Negative: Frown,
  'Very positive': SmilePlus,
  'Very negative': Angry,
  Mixed: Meh,
  None: Meh
};
function resolveSentimentType(sentimentLabel: string | undefined): SentimentType {
  const raw = sentimentLabel?.trim() ?? '';
  if (raw in sentimentHex) return raw as SentimentType;
  const humanized = capitalize(raw.replace(/_/g, ' '));
  if (humanized in sentimentHex) return humanized as SentimentType;
  return 'Neutral';
}
export const SentimentChipByLabel = t0 => {
  const $ = _c(17);
  const {
    sentimentLabel,
    className,
    clickable: t1
  } = t0;
  const clickable = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== sentimentLabel) {
    t2 = resolveSentimentType(sentimentLabel);
    $[0] = sentimentLabel;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const label = t2;
  const Icon = sentimentIconComponents[label];
  const color = sentimentHex[label];
  const t3 = clickable && "cursor-pointer hover:opacity-80";
  let t4;
  if ($[2] !== className || $[3] !== t3) {
    t4 = cn("inline-flex w-max shrink-0 items-center gap-1.5 rounded-full border bg-transparent px-1.5 py-1 text-[12px] leading-4 whitespace-nowrap text-foreground", t3, className);
    $[2] = className;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const t5 = `${color}66`;
  const t6 = `${color}66`;
  let t7;
  if ($[5] !== t5 || $[6] !== t6) {
    t7 = {
      borderColor: t5,
      color: t6
    };
    $[5] = t5;
    $[6] = t6;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== Icon) {
    t8 = <Icon size={12} />;
    $[8] = Icon;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== label) {
    t9 = <span className="min-w-max text-white">{label}</span>;
    $[10] = label;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== t4 || $[13] !== t7 || $[14] !== t8 || $[15] !== t9) {
    t10 = <span className={t4} style={t7}>{t8}{t9}</span>;
    $[12] = t4;
    $[13] = t7;
    $[14] = t8;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  return t10;
};
function _temp(e) {
  return e.stopPropagation();
}
function _temp2() {}
function _temp3(e) {
  return <div className="flex items-center" key={e}><Checkbox checked={true} className="h-3 w-3" /><p className="pl-1">{e}</p></div>;
}
function _temp4(e) {
  return <div className="flex items-center" key={e}><Checkbox checked={true} className="h-3 w-3" /><p className="pl-1">{capitalize(e)}</p></div>;
}
function _temp5(e) {
  return <div className="flex items-center" key={e}><Checkbox checked={true} className="h-3 w-3" /><p className="pl-1">{capitalize(e)}</p></div>;
}
function _temp6(mode_0) {
  if (mode_0 === "user_action") {
    return "action";
  }
  if (mode_0 === "topic") {
    return "topic";
  }
  if (mode_0 === "user_intent") {
    return "intent";
  }
}